import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import HistoryIcon from '@mui/icons-material/History';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../contexts/AuthContext';
import { ModelSelector } from '../components/ModelSelector';
import { TemperatureSlider } from '../components/TemperatureSlider';
import { getAllModelOptions, MODEL_REGISTRY, supportsEffort } from '../config/modelConfig';
import { AppBuilderPane } from './playground/AppBuilderPane';
import { BuildTierPicker, STUDIO_BUILD_MODELS, STUDIO_DEFAULT_MODEL } from './playground/BuildTierPicker';
import { EFFORT_OPTIONS, EffortPicker } from './playground/EffortPicker';
import { ChatMarkdown } from './playground/ChatMarkdown';
import { CompareReply } from './playground/CompareReply';
import { WorkingIndicator } from './playground/WorkingIndicator';
import {
  cachePlaygroundMessages,
  extractProjectFilesLite,
  builderReplyMissedCanvas,
  formatCredits,
  formatShortDate,
  formatWhen,
  byUpdatedDesc,
  isImageGenModel,
  looksLikeImageRequest,
  mergePlaygroundMessages,
  modelCanSeeImages,
  readSseResponse,
  suggestedImageModels,
} from './playground/helpers';
import { ProjectState, projectLooksImported, clearPreviewStore } from './playground/previewHtml';
import { PG, playgroundMuiTheme, pgBtn } from './playground/theme';
import { getConfig } from '../services/config';
import { useNavigate } from 'react-router-dom';
import { useViewMode } from '../contexts/ViewModeContext';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ArticleIcon from '@mui/icons-material/Article';
import { NutritionApiBrowser } from './playground/NutritionApiBrowser';
import { NutritionDocsBrowser } from './playground/NutritionDocsBrowser';
import { ChainPicker } from './playground/ChainPicker';
import {
  NutritionRoute,
  filterNutritionRoutes,
  insertNutritionMention,
  mentionAtCaret,
  chainMentionSlug,
  nutritionRouteAlias,
  nutritionRouteKey,
} from './playground/nutritionRoutes';
import { ConfirmDialog } from './playground/ConfirmDialog';
import {
  STUDIO_TARGETS,
  StudioWizard,
  StudioPlatform,
  StudioWizardResult,
  studioTargetFor,
} from './playground/StudioWizard';
import { ContextExportWizard, type ContextExportInitial } from './playground/ContextExportWizard';
import IosShareIcon from '@mui/icons-material/IosShare';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';

type CatalogHit = {
  type: string;
  id: string;
  name: string;
  description?: string | null;
  updated_at?: string | null;
};

type Attachment = {
  url: string;
  filename?: string;
  content_type?: string;
  kind?: string;
};

type Message = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  attachments?: Attachment[] | null;
  artifacts?: { kind?: string; url?: string }[] | null;
  model_used?: string | null;
  usage?: { credits_used?: number } | null;
  settings_snapshot?: Record<string, any> | null;
  created_at?: string;
};

type Session = {
  id: string;
  title: string;
  settings: Record<string, any>;
  updated_at: string;
  message_count: number;
};

function downloadFilename(res: Response, fallback: string): string {
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="([^"]+)"/i) || cd.match(/filename=([^;]+)/i);
  const name = (m?.[1] || '').trim().replace(/^UTF-8''/, '');
  return name || fallback;
}

function emptyProject(): ProjectState {
  return { files: {}, preview_path: 'index.html', notes: '', notes_log: [] };
}

function sessionFileCount(s: Session, live?: ProjectState): number {
  const files = (s.id && live ? live.files : s.settings?.project?.files) || {};
  return Object.keys(files).length;
}

function sessionLooksBuilder(s: Session, currentId: string | null, builderNow: boolean): boolean {
  if (s.id === currentId) return builderNow;
  return (s.settings?.mode || '') === 'app_builder';
}

const DEFAULT_SETTINGS = {
  model: STUDIO_DEFAULT_MODEL,
  temperature: 0.7,
  intelligence: 3,
  category_ids: [] as string[],
  object_ids: [] as string[],
  chain_ids: [] as string[],
  function_ids: [] as string[],
  system_prompt: '',
  top_k: 6,
  mode: 'chat',
  target_languages: ['html', 'css', 'javascript'] as string[],
  project: emptyProject() as ProjectState,
  nutrition_ai: false,
  nutrition_license_key: '',
  nutrition_route_ids: [] as string[],
  nutrition_doc_urls: [] as string[],
  sdk_dev: false,
  sdk_platforms: [] as string[],
  sdk_doc_urls: [] as string[],
  sdk_capability_ids: [] as string[],
  smart_context: false,
};

/** Settings dialog tabs. Shared knobs live on `general`; the rest are per-platform. */
type SettingsTab = 'general' | 'easyai' | 'nutrition' | 'sdk';

const SETTINGS_TABS: { id: SettingsTab; label: string; dot: string }[] = [
  { id: 'general', label: 'General', dot: PG.accent },
  { id: 'easyai', label: 'EasyAI', dot: '#38bdf8' },
  { id: 'nutrition', label: 'NutritionAI', dot: '#34d399' },
  { id: 'sdk', label: 'Mobile SDK', dot: '#a78bfa' },
];

/** Last path segment of a pinned GitBook URL, for a readable chip. */
function docUrlLabel(url: string): string {
  const seg = (url || '').replace(/\.md$/, '').split('/').filter(Boolean).pop() || url;
  return seg.replace(/-/g, ' ').slice(0, 34);
}

/** Mobile SDKs the Studio has knowledge packs for. `langs` seed `target_languages`. */
// `aliases` must stay in sync with _SDK_PLATFORM_ALIASES in app_builder/knowledge.py, so a
// `sdk_platform=` deep link and a saved sdk_platforms value resolve the same way. `langs` is a
// different thing: the builder target languages to seed, which is why they are listed separately.
export const SDK_PLATFORMS: { id: string; label: string; langs: string[]; aliases: string[]; blurb: string }[] = [
  { id: 'ios', label: 'iOS (Swift)', langs: ['swift'], aliases: ['swift', 'iphone'], blurb: 'SPM package, iOS 13+. Camera needs a real device.' },
  { id: 'android', label: 'Android (Kotlin)', langs: ['kotlin'], aliases: ['kotlin', 'java'], blurb: 'Maven Central, minSdk 26, compileSdk 35.' },
  { id: 'react_native', label: 'React Native', langs: ['react', 'typescript'], aliases: ['react-native', 'reactnative', 'rn'], blurb: 'GitHub Packages registry — needs an npm auth token.' },
  { id: 'flutter', label: 'Flutter (Dart)', langs: ['flutter'], aliases: ['dart'], blurb: 'pub.dev, Flutter 3.3+, Dart 3.0.6+.' },
];

export const sdkPlatformLabel = (id: string): string =>
  SDK_PLATFORMS.find((p) => p.id === id)?.label || id;

/** Resolve a `sdk_platform=` param (id or alias) onto a pack id. */
export const resolveSdkPlatform = (raw: string): string | undefined => {
  const key = raw.trim().toLowerCase().replace(/ /g, '_');
  return SDK_PLATFORMS.find((p) => p.id === key || p.aliases.includes(key))?.id;
};

/** Previous Studio default — too weak (and now deprecated) for building an app. */
const LEGACY_STUDIO_MODELS = new Set(['gemini-2-5-flash', 'gemini-2-5-flash-lite']);

function withStudioModel(s: Record<string, any>): Record<string, any> {
  const model = String(s.model || '');
  if (!model || LEGACY_STUDIO_MODELS.has(model)) {
    return { ...s, model: DEFAULT_SETTINGS.model };
  }
  return s;
}

/** Studio's default before Opus 5.5. New chats move off it once; choosing it again afterwards sticks. */
const PREVIOUS_DEFAULT_MODEL = 'gpt-5.5';
const DEFAULT_MODEL_SEEN_KEY = 'studio.defaultModel';

function carriedModel(prev?: string): string {
  if (!prev) return DEFAULT_SETTINGS.model;
  if (prev !== PREVIOUS_DEFAULT_MODEL) return prev;
  try {
    if (localStorage.getItem(DEFAULT_MODEL_SEEN_KEY) === DEFAULT_SETTINGS.model) return prev;
    localStorage.setItem(DEFAULT_MODEL_SEEN_KEY, DEFAULT_SETTINGS.model);
  } catch {
    return prev;
  }
  return DEFAULT_SETTINGS.model;
}

function freshSessionSettings(prev: Record<string, any>, mode?: string): Record<string, any> {
  return {
    ...DEFAULT_SETTINGS,
    mode: mode || prev.mode || DEFAULT_SETTINGS.mode,
    model: carriedModel(prev.model),
    temperature: typeof prev.temperature === 'number' ? prev.temperature : DEFAULT_SETTINGS.temperature,
    target_languages: Array.isArray(prev.target_languages)
      ? prev.target_languages
      : DEFAULT_SETTINGS.target_languages,
    nutrition_ai: !!prev.nutrition_ai,
    nutrition_license_key: prev.nutrition_license_key || '',
    // Carry the platform choice into a new app — switching back every time is just friction.
    sdk_dev: !!prev.sdk_dev,
    sdk_platforms: Array.isArray(prev.sdk_platforms) ? prev.sdk_platforms : [],
    project: emptyProject(),
    chain_ids: [],
    function_ids: [],
    object_ids: [],
    category_ids: [],
    chain_builder_context: null,
  };
}

const BUILDER_LANG_GROUPS: { label: string; langs: { id: string; label: string }[] }[] = [
  {
    label: 'Web (live canvas preview)',
    langs: [
      { id: 'html', label: 'HTML' },
      { id: 'css', label: 'CSS' },
      { id: 'javascript', label: 'JavaScript' },
      { id: 'typescript', label: 'TypeScript' },
      { id: 'react', label: 'React' },
      { id: 'vue', label: 'Vue' },
      { id: 'svelte', label: 'Svelte' },
    ],
  },
  {
    label: 'Mobile',
    langs: [
      { id: 'swift', label: 'Swift' },
      { id: 'kotlin', label: 'Kotlin' },
      { id: 'flutter', label: 'Flutter / Dart' },
    ],
  },
  {
    label: 'Backend / other',
    langs: [
      { id: 'python', label: 'Python' },
      { id: 'node', label: 'Node' },
      { id: 'go', label: 'Go' },
      { id: 'java', label: 'Java' },
      { id: 'csharp', label: 'C#' },
    ],
  },
];

const ATTACH_HELP: Record<string, string> = {
  chain: 'Attached for the next message — graph, steps, and inputs go with the prompt. Describe the app. Preview can run this chain as you while you are logged in.',
  object: 'The next reply is structured to this object’s JSON schema.',
  function: 'The assistant sees this AI function’s name and description.',
  category: 'Retrieves matching chunks from this knowledge category on each send.',
};

const SKIP_IMPORT = /(^|\/)(node_modules|\.git|dist|build|__pycache__|\.next|coverage|vendor|\.ds_store)(\/|$)/i;
const TEXT_IMPORT = /\.(html?|css|js|mjs|cjs|jsx|tsx?|json|md|txt|py|go|java|kt|swift|dart|vue|svelte|cs|xml|yml|yaml|toml|svg|sh|ps1|dockerfile|gitignore|env|example)$/i;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Read a NutritionAI license handed over by the Passio accounts page.
 *
 * The license arrives in the URL **fragment** (`#nutrition_key=...`), never the query
 * string: fragments are not sent to the server, so the key cannot land in nginx access
 * logs or a Referer header. The caller strips the fragment immediately after reading it.
 */
function readLicenseFromHash(): string {
  if (typeof window === 'undefined') return '';
  const raw = (window.location.hash || '').replace(/^#/, '');
  if (!raw) return '';
  try {
    const frag = new URLSearchParams(raw);
    return (frag.get('nutrition_key') || frag.get('license') || '').trim();
  } catch {
    return '';
  }
}

function isStockBuilderPrompt(text: string): boolean {
  const t = (text || '').trim();
  if (!t) return true;
  return (
    /^Add a page for the attached chain/.test(t) ||
    /^Build a (polished|complete|small) (web )?app/.test(t) ||
    /^Look at the imported project/.test(t)
  );
}

function studioEasyAiBase(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  if (host === 'localhost' || host === '127.0.0.1') return 'https://easyai-dev.cloud.passiolife.com';
  if (host.includes('easyai-dev')) return origin || 'https://easyai-dev.cloud.passiolife.com';
  if (host === 'easyai.passiolife.com' || host.endsWith('.easyai.passiolife.com')) {
    return origin || 'https://easyai.passiolife.com';
  }
  const api = (getConfig().apiBaseUrl || '').replace(/\/+$/, '');
  if (api) return api;
  return origin || 'https://easyai.passiolife.com';
}

function studioNutritionBase(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host === 'localhost' || host === '127.0.0.1' || host.includes('easyai-dev')) {
    return 'https://api-dev.passiolife.com';
  }
  return 'https://api.passiolife.com';
}

function chainBuildPrompt(name: string, extendExisting: boolean): string {
  if (extendExisting) {
    return `Add a page for the attached chain “${name}” into this imported app. Match current layout, colors, and env/config names. Do not replace unrelated files. Form = data-page="input", wait/progress = data-page="waiting", parsed results = data-page="results". Read PASSIO_CONFIG.demoMode at call time. When demoMode is false, still fetch even if easyAiKey is a placeholder — Studio preview supplies auth.`;
  }
  return `Build a complete web app for “${name}” (replace any current preview files — this is a new app, not an extra page). Form for its inputs (data-page="input"), a wait/progress view while the chain runs (data-page="waiting" + is-visible class), then parse and display the results as cards (data-page="results"). Header with a logo slot (assets/logo.png). Use PASSIO_CONFIG.easyAiKey, easyAiBase, and chainId. Always event.preventDefault() on the form (first line) and call execute+poll with fetch — never a native form POST. Read demoMode at call time. When demoMode is false, fetch even if the key is still YOUR_EASYAI_API_KEY (Studio preview proxies as the logged-in user). Mock a completed run when demoMode is true for export. Show waiting before fetch and keep it up until poll completes. Do not recreate the chain.`;
}

type IntelFilter = 'all' | 'light' | 'balanced' | 'strong' | 'max';

const INTEL_FILTERS: { id: IntelFilter; label: string; match?: (n: number) => boolean }[] = [
  { id: 'all', label: 'All' },
  { id: 'light', label: 'Lightweight', match: (n) => n <= 2 },
  { id: 'balanced', label: 'Mid-range', match: (n) => n === 3 },
  { id: 'strong', label: 'Advanced', match: (n) => n === 4 },
  { id: 'max', label: 'Top tier', match: (n) => n >= 5 },
];

function gitbookChipLabel(url: string): string {
  try {
    const path = new URL(url).pathname.replace(/^\/nutrition-ai\//, '').replace(/\.md$/, '');
    const last = path.split('/').filter(Boolean).pop() || 'GitBook';
    const label = last.replace(/-/g, ' ');
    return label.length > 28 ? `docs · ${label.slice(0, 26)}…` : `docs · ${label}`;
  } catch {
    return 'GitBook';
  }
}

function uniqueIds(ids: unknown): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of Array.isArray(ids) ? ids : []) {
    const id = String(raw || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function withUniqueAttach(s: Record<string, any>): Record<string, any> {
  const next: Record<string, any> = {
    ...s,
    chain_ids: uniqueIds(s.chain_ids),
    function_ids: uniqueIds(s.function_ids),
    object_ids: uniqueIds(s.object_ids),
    category_ids: uniqueIds(s.category_ids),
    nutrition_route_ids: uniqueIds(s.nutrition_route_ids),
    nutrition_doc_urls: uniqueIds(s.nutrition_doc_urls),
    sdk_platforms: uniqueIds(s.sdk_platforms),
    sdk_doc_urls: uniqueIds(s.sdk_doc_urls),
    sdk_capability_ids: uniqueIds(s.sdk_capability_ids),
  };
  const ctxId = next.chain_builder_context?.id;
  if (ctxId && next.chain_ids.includes(ctxId)) {
    next.chain_builder_context = null;
  }
  return next;
}

function messageHasAppFiles(m: Message): boolean {
  const files = m.settings_snapshot?.project?.files;
  if (files && typeof files === 'object' && Object.keys(files).length > 0) return true;
  const c = m.content || '';
  if (/```[^\n]*\bpath\s*=/.test(c) || /<!DOCTYPE html/i.test(c)) return true;
  return extractProjectFilesLite(c).length > 0;
}

type ChatBlock =
  | { kind: 'user'; message: Message }
  | { kind: 'assistant'; message: Message }
  | { kind: 'compare'; groupId: string; lanes: Message[] };

function blocksFromMessages(messages: Message[]): ChatBlock[] {
  const blocks: ChatBlock[] = [];
  messages.forEach((m) => {
    const gid = m.role === 'assistant' ? String(m.settings_snapshot?.compare_group || '') : '';
    if (m.role === 'assistant' && gid) {
      const last = blocks[blocks.length - 1];
      if (last && last.kind === 'compare' && last.groupId === gid) {
        last.lanes.push(m);
      } else {
        blocks.push({ kind: 'compare', groupId: gid, lanes: [m] });
      }
    } else if (m.role === 'user') {
      blocks.push({ kind: 'user', message: m });
    } else {
      blocks.push({ kind: 'assistant', message: m });
    }
  });
  return blocks;
}

export const Playground: React.FC = () => {
  const { getIdToken, getEffectiveCustomerId } = useAuth();
  const navigate = useNavigate();
  const { setViewMode } = useViewMode();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [settings, setSettings] = useState<Record<string, any>>(DEFAULT_SETTINGS);
  const [input, setInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState<Attachment[]>([]);
  const [sending, setSending] = useState(false);
  const [queued, setQueued] = useState<{ text: string; attachments: Attachment[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [catalogQ, setCatalogQ] = useState('');
  const [catalogHits, setCatalogHits] = useState<CatalogHit[]>([]);
  const [categories, setCategories] = useState<CatalogHit[]>([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [attachKind, setAttachKind] = useState<'chain' | 'function' | 'object'>('chain');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<SettingsTab>('general');
  const [sdkPickerOpen, setSdkPickerOpen] = useState(false);
  const [sdkCapsOpen, setSdkCapsOpen] = useState(false);
  const [nutritionCatalogOpen, setNutritionCatalogOpen] = useState(false);
  const [nutritionDocsOpen, setNutritionDocsOpen] = useState(false);
  const [sdkDocsOpen, setSdkDocsOpen] = useState(false);
  const [chainPickerOpen, setChainPickerOpen] = useState(false);
  const [napiRoutes, setNapiRoutes] = useState<NutritionRoute[]>([]);
  // Same rows as napiRoutes but annotated with SDK methods, and loaded in SDK mode —
  // where napiRoutes stays empty because the two platforms are mutually exclusive.
  const [sdkCapRoutes, setSdkCapRoutes] = useState<NutritionRoute[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [workspace, setWorkspace] = useState<'chat' | 'split' | 'canvas'>('split');
  const [intelFilter, setIntelFilter] = useState<IntelFilter>('all');
  const [chatPct, setChatPct] = useState(36);
  const [streamingText, setStreamingText] = useState('');
  const [thinkingText, setThinkingText] = useState('');
  const [streamStartedAt, setStreamStartedAt] = useState<number | null>(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatingAsset, setGeneratingAsset] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareModels, setCompareModels] = useState<string[]>([]);
  const [compareSel, setCompareSel] = useState<Record<string, string>>({});
  const [compareStream, setCompareStream] = useState<{ groupId: string; model: string; text: string } | null>(null);
  const [replayUserId, setReplayUserId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportingCursor, setExportingCursor] = useState(false);
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const splitRef = useRef<HTMLDivElement>(null);
  const draggingSplit = useRef(false);
  const pinToBottom = useRef(true);
  const fileRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const sendingRef = useRef(false);
  const queuedRef = useRef<{ text: string; attachments: Attachment[] } | null>(null);
  const skipQueueDrainRef = useRef(false);
  const sendRef = useRef<(
    overrideText?: string,
    extraAttachments?: Attachment[],
    replaceAttachments?: boolean,
  ) => Promise<void>>(async () => {});
  const bootRef = useRef(false);
  const [canvasHint, setCanvasHint] = useState<string | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [contextExportOpen, setContextExportOpen] = useState(false);
  // Set from `?platform=` on a deep link — when present the wizard skips its mode picker.
  const [wizardPlatformHint, setWizardPlatformHint] = useState<StudioPlatform | null>(null);
  // NutritionAI license handed over in the URL fragment (never sent to the server).
  const [wizardLicensePrefill, setWizardLicensePrefill] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Session | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [restoreAsk, setRestoreAsk] = useState<string | null>(null);
  const [importAsk, setImportAsk] = useState<FileList | null>(null);
  const isBuilder = (settings.mode || 'chat') === 'app_builder';
  // Not gated on isBuilder: "which route should I use for X?" is a chat question, and the
  // backend loads the route catalog in both modes (load_nutrition_chat_knowledge). Gating
  // it here would leave chat with the knowledge but no route browser and no @ picker.
  // Only the *preview* proxy is builder-only — see liveNutrition below.
  const nutritionOn = !!settings.nutrition_ai;
  const nutritionKeySet = !!(settings.nutrition_license_key || '').trim();
  // Same reasoning: integration questions are the main reason people open SDK mode.
  const sdkOn = !!settings.sdk_dev;
  const sdkPlatforms: string[] = settings.sdk_platforms || [];
  const sdkCapabilityIds: string[] = settings.sdk_capability_ids || [];
  /** SDK method name for a pinned capability, falling back to the route summary. */
  const sdkCapabilityLabel = (id: string): string => {
    const hit = sdkCapRoutes.find((r) => nutritionRouteKey(r) === id);
    return hit?.sdk_method || hit?.summary || id;
  };
  const project: ProjectState = settings.project || { files: {}, preview_path: 'index.html' };
  const isImageModel = isImageGenModel(settings.model);
  const imageHint = looksLikeImageRequest(input) && !isImageModel;
  const imageChoices = useMemo(() => suggestedImageModels(), []);
  const canSeeImages = modelCanSeeImages(settings.model);
  const showChatPane = !isBuilder || workspace !== 'canvas';
  const showCanvas = isBuilder && workspace !== 'chat';
  const modelOptions = useMemo(() => {
    const all = getAllModelOptions();
    const rule = INTEL_FILTERS.find((f) => f.id === intelFilter)?.match;
    if (!rule) return all;
    const filtered = all.filter((o) => rule(o.intelligenceRating ?? 3));
    const current = all.find((o) => o.value === settings.model);
    if (current && !filtered.some((o) => o.value === current.value)) {
      return [current, ...filtered];
    }
    return filtered;
  }, [intelFilter, settings.model]);
  const textModelOptions = useMemo(
    () => modelOptions.filter((o) => o.category === 'text'),
    [modelOptions],
  );
  const mentionHits = useMemo(() => {
    if (mentionQuery == null) return [] as Array<
      | { kind: 'chain'; hit: CatalogHit; latest?: boolean }
      | { kind: 'function'; hit: CatalogHit; latest?: boolean }
      | { kind: 'route'; route: NutritionRoute }
    >;
    const needle = mentionQuery.trim().toLowerCase();
    const matchHit = (h: CatalogHit) => {
      if (!needle) return true;
      return `${h.name} ${h.description || ''} ${chainMentionSlug(h.name)}`.toLowerCase().includes(needle);
    };
    const markLatest = (rows: CatalogHit[]) => {
      const seen = new Set<string>();
      return rows.map((hit) => {
        const key = (hit.name || '').toLowerCase();
        const latest = !seen.has(key);
        if (key) seen.add(key);
        return { hit, latest };
      });
    };
    const chains = markLatest(
      catalogHits.filter((h) => h.type === 'chain').filter(matchHit).sort(byUpdatedDesc),
    )
      .slice(0, 8)
      .map(({ hit, latest }) => ({ kind: 'chain' as const, hit, latest }));
    const fns = markLatest(
      catalogHits.filter((h) => h.type === 'function').filter(matchHit).sort(byUpdatedDesc),
    )
      .slice(0, 4)
      .map(({ hit, latest }) => ({ kind: 'function' as const, hit, latest }));
    // SDK mode offers the same rows under their SDK method names — the browse dialog
    // promises "you can also type @name", so the picker has to honour it in both modes.
    const routeSource = sdkOn ? sdkCapRoutes : nutritionOn ? napiRoutes : [];
    const routes = routeSource.length
      ? filterNutritionRoutes(routeSource, mentionQuery)
          .slice(0, 6)
          .map((route) => ({ kind: 'route' as const, route }))
      : [];
    return [...chains, ...fns, ...routes].slice(0, 12);
  }, [mentionQuery, catalogHits, napiRoutes, sdkCapRoutes, nutritionOn, sdkOn]);
  const contextExportInitial = useMemo((): ContextExportInitial => {
    const platform = sdkOn ? 'sdk' : nutritionOn ? 'nutrition' : 'easyai';
    const title = sessions.find((s) => s.id === sessionId)?.title || '';
    return {
      platform,
      title: title && title !== 'New chat' ? title : '',
      chainIds: uniqueIds(settings.chain_ids || []),
      functionIds: uniqueIds(settings.function_ids || []),
      objectIds: uniqueIds(settings.object_ids || []),
      routeIds: platform === 'sdk' ? settings.sdk_capability_ids || [] : platform === 'nutrition' ? settings.nutrition_route_ids || [] : [],
      docUrls: platform === 'sdk' ? settings.sdk_doc_urls || [] : platform === 'nutrition' ? settings.nutrition_doc_urls || [] : [],
      sdkPlatforms: settings.sdk_platforms || [],
    };
  }, [sdkOn, nutritionOn, sessions, sessionId, settings]);
  const pinnedNutritionRoutes = useMemo(() => {
    const ids = new Set((settings.nutrition_route_ids || []).map(String));
    return napiRoutes.filter((r) => ids.has(nutritionRouteKey(r)));
  }, [napiRoutes, settings.nutrition_route_ids]);

  const authHeaders = useCallback(async (): Promise<HeadersInit> => {
    const token = await getIdToken();
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getIdToken]);

  const loadSessions = useCallback(async () => {
    const headers = await authHeaders();
    const res = await fetch('/api/v2/playground/sessions', { headers });
    if (!res.ok) throw new Error('Failed to list chats');
    const data: Session[] = await res.json();
    setSessions(data);
    return data;
  }, [authHeaders]);

  const loadSession = useCallback(async (id: string) => {
    const headers = await authHeaders();
    const res = await fetch(`/api/v2/playground/sessions/${id}`, { headers });
    if (!res.ok) throw new Error('Failed to load chat');
    const data = await res.json();
    setSessionId(data.id);
    setMessages(mergePlaygroundMessages(id, data.messages || []));
    const next = withStudioModel(withUniqueAttach({ ...DEFAULT_SETTINGS, ...(data.settings || {}) }));
    setSettings(next);
    if (next.model !== (data.settings || {}).model) {
      fetch(`/api/v2/playground/sessions/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ settings: next }),
      }).catch(() => undefined);
    }
    setStreamingText('');
    setGeneratingImage(false);
    setNotice(null);
    setError(null);
    setPendingFiles([]);
    setCompareStream(null);
    setReplayUserId(null);
    skipQueueDrainRef.current = true;
    abortRef.current?.abort();
    sendingRef.current = false;
    setSending(false);
    queuedRef.current = null;
    setQueued(null);
    pinToBottom.current = true;
  }, [authHeaders]);

  const loadCatalog = useCallback(async (q: string) => {
    const headers = await authHeaders();
    const [allRes, catRes] = await Promise.all([
      fetch(`/api/v2/playground/catalog?q=${encodeURIComponent(q)}`, { headers }),
      fetch(`/api/v2/playground/catalog?types=category`, { headers }),
    ]);
    if (allRes.ok) setCatalogHits(await allRes.json());
    if (catRes.ok) setCategories(await catRes.json());
  }, [authHeaders]);

  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;
    (async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams(window.location.search);
        const wantNew = params.get('new') === '1';
        const chain = (params.get('chain') || '').trim();
        const chainName = (params.get('name') || '').trim();
        const modeParam = params.get('mode');
        const platformParam = (params.get('platform') || '').trim().toLowerCase();
        const wizardParam = params.get('wizard');
        // `?import=1` — "work on the app I already have". A URL cannot open a file picker
        // (browsers require a user gesture), so this lands them in App Builder with the
        // canvas open and points at the Import control rather than doing it for them.
        const wantImport = params.get('import') === '1';
        // Fragment-only handoff (see readLicenseFromHash).
        const handoffLicense = readLicenseFromHash();

        const wantsNutrition = platformParam === 'nutrition' || platformParam === 'nutritionai';
        const wantsSdk = platformParam === 'sdk' || platformParam === 'mobilesdk';
        const platformHint: StudioPlatform | null = wantsNutrition
          ? 'nutrition'
          : wantsSdk
            ? 'sdk'
            : platformParam === 'easyai'
              ? 'easyai'
              : null;
        // `?sdk_platform=ios` (or any alias the backend accepts) preselects the SDK, so a deep
        // link from a platform-specific docs page lands ready to go.
        const sdkPlatformParam = resolveSdkPlatform(params.get('sdk_platform') || '');
        const sdkPlatformSeed = SDK_PLATFORMS.find((p) => p.id === sdkPlatformParam);
        // A `platform=` deep link is an onboarding entry point, so the walkthrough opens
        // unless it is explicitly suppressed with wizard=0.
        const wantWizard = wizardParam === '1' || (!!platformHint && wizardParam !== '0');

        const list = await loadSessions();
        await loadCatalog('');
        // NutritionAI only takes effect in App Builder, so asking for it implies that mode.
        const mode =
          modeParam === 'chat'
            ? 'chat'
            : modeParam === 'app_builder' || chain || wantsNutrition || wantImport
              ? 'app_builder'
              : undefined;
        const platformSeed = {
          ...(wantsNutrition ? { nutrition_ai: true } : {}),
          ...(handoffLicense ? { nutrition_license_key: handoffLicense } : {}),
          ...(wantsSdk
            ? {
                sdk_dev: true,
                nutrition_ai: false,
                ...(sdkPlatformSeed
                  ? { sdk_platforms: [sdkPlatformSeed.id], target_languages: sdkPlatformSeed.langs }
                  : {}),
              }
            : {}),
        };

        if (wantWizard) {
          setWizardPlatformHint(platformHint);
          setWizardLicensePrefill(handoffLicense);
        }

        // Create a session when asked to, and also when a deep link needs somewhere to
        // land but the account has no sessions yet.
        const needsCreate = wantNew || !!chain || (wantWizard && !list[0]);

        if (needsCreate) {
          const seed = {
            ...DEFAULT_SETTINGS,
            ...(mode ? { mode } : {}),
            ...(chain ? { chain_ids: [chain] } : {}),
            ...platformSeed,
            project: emptyProject(),
          };
          const headers = await authHeaders();
          const res = await fetch('/api/v2/playground/sessions', {
            method: 'POST',
            headers,
            body: JSON.stringify({
              title: seed.mode === 'app_builder' ? (chainName ? `${chainName} app` : 'New app') : 'New chat',
              settings: seed,
            }),
          });
          if (res.ok) {
            const created: Session = await res.json();
            setSessions((prev) => [created, ...prev.filter((s) => s.id !== created.id)]);
            setSessionId(created.id);
            setMessages([]);
            setSettings(withUniqueAttach({ ...DEFAULT_SETTINGS, ...seed, ...(created.settings || {}) }));
            if (chain) setInput(chainBuildPrompt(chainName || 'this chain', false));
          } else if (list[0]) {
            await loadSession(list[0].id);
          }
        } else if (list[0]) {
          await loadSession(list[0].id);
          const patch = { ...(mode ? { mode } : {}), ...platformSeed };
          if (Object.keys(patch).length) {
            const headers = await authHeaders();
            await fetch(`/api/v2/playground/sessions/${list[0].id}`, {
              method: 'PATCH',
              headers,
              body: JSON.stringify({ settings: patch }),
            });
            setSettings((prev) => withUniqueAttach({ ...prev, ...patch }));
          }
        }
        if (wantImport) {
          setWorkspace('split');
          setNotice(
            'Click "Import folder" above the canvas and choose your project. Studio reads your ' +
              'files, then works alongside them — it matches your layout, styles and env var names ' +
              'and only touches what the Passio integration needs.',
          );
        }
        if (wantWizard) setWizardOpen(true);
        // Always drop the query AND the fragment: setting only the pathname clears both,
        // so a handed-over license never lingers in the address bar or browser history.
        if (wantNew || chain || modeParam || platformParam || wizardParam || handoffLicense || wantImport) {
          window.history.replaceState({}, '', window.location.pathname);
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load Playground');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadSessions, loadSession, loadCatalog, authHeaders]);

  useEffect(() => {
    if (sessionId && messages.length) cachePlaygroundMessages(sessionId, messages);
  }, [sessionId, messages]);

  useEffect(() => {
    if (!nutritionOn) {
      setNapiRoutes([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch('/api/v2/playground/nutrition/openapi', { headers });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || cancelled) return;
        setNapiRoutes(Array.isArray(body.routes) ? body.routes : []);
      } catch {
        if (!cancelled) setNapiRoutes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nutritionOn, authHeaders]);

  // SDK mode needs the same catalog annotated with SDK methods, so pinned chips can show
  // the method name and `@` tagging works. Separate from napiRoutes because the platforms
  // are mutually exclusive, so only one of the two is ever populated.
  useEffect(() => {
    if (!sdkOn) {
      setSdkCapRoutes([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch('/api/v2/playground/sdk/capabilities', { headers });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || cancelled) return;
        setSdkCapRoutes(Array.isArray(body.routes) ? body.routes : []);
      } catch {
        if (!cancelled) setSdkCapRoutes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sdkOn, authHeaders]);

  useEffect(() => {
    if (!pinToBottom.current) return;
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streamingText, generatingImage, sending, thinkingText]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!draggingSplit.current || !splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setChatPct(Math.min(70, Math.max(22, pct)));
    };
    const onUp = () => {
      draggingSplit.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const persistSettings = async (next: Record<string, any>, id = sessionId) => {
    if (!id) return;
    const payload = withUniqueAttach(next);
    const headers = await authHeaders();
    await fetch(`/api/v2/playground/sessions/${id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ settings: payload }),
    });
  };

  const finishSending = () => {
    sendingRef.current = false;
    setSending(false);
    setStreamStartedAt(null);
    setThinkingText('');
    if (skipQueueDrainRef.current) {
      skipQueueDrainRef.current = false;
      return;
    }
    const q = queuedRef.current;
    if (!q) return;
    queuedRef.current = null;
    setQueued(null);
    window.setTimeout(() => {
      void sendRef.current(q.text, q.attachments, true);
    }, 0);
  };

  const newChat = async (overrides?: Record<string, any>): Promise<string | null> => {
    if (sessionId) {
      skipQueueDrainRef.current = true;
      abortRef.current?.abort();
      sendingRef.current = false;
      setSending(false);
      queuedRef.current = null;
      setQueued(null);
    }
    const seed = {
      ...withStudioModel(freshSessionSettings(settings, isBuilder ? 'app_builder' : settings.mode)),
      ...(overrides || {}),
    };
    const headers = await authHeaders();
    const res = await fetch('/api/v2/playground/sessions', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        title: seed.mode === 'app_builder' ? 'New app' : 'New chat',
        settings: seed,
      }),
    });
    if (!res.ok) {
      setError('Could not create chat');
      return null;
    }
    const created: Session = await res.json();
    setSessions((prev) => [created, ...prev]);
    setSessionId(created.id);
    setMessages([]);
    setStreamingText('');
    setGeneratingImage(false);
    setNotice(null);
    setError(null);
    setPendingFiles([]);
    setCompareOpen(false);
    setCompareStream(null);
    setReplayUserId(null);
    setInput('');
    setSettingsOpen(false);
    setSettings(
      withUniqueAttach({
        ...seed,
        ...(created.settings || {}),
        project: emptyProject(),
        chain_ids: [],
        function_ids: [],
        object_ids: [],
        category_ids: [],
        chain_builder_context: null,
      }),
    );
    pinToBottom.current = true;
    return created.id;
  };

  const deleteChat = async (id: string) => {
    const headers = await authHeaders();
    const res = await fetch(`/api/v2/playground/sessions/${id}`, { method: 'DELETE', headers });
    if (!res.ok) throw new Error((await res.text()) || 'Could not delete');
    clearPreviewStore(id);
    const next = sessions.filter((s) => s.id !== id);
    setSessions(next);
    if (sessionId === id) {
      if (next[0]) await loadSession(next[0].id);
      else {
        setSessionId(null);
        setMessages([]);
        setStreamingText('');
        setNotice(null);
        setSettings({ ...DEFAULT_SETTINGS, project: emptyProject() });
      }
    }
  };

  const updateSetting = async (patch: Record<string, any>) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    await persistSettings(next);
  };

  /** Apply the Get-started walkthrough's answers to this session. */
  const applyWizard = async (result: StudioWizardResult) => {
    const nutrition = result.platform === 'nutrition';
    const sdk = result.platform === 'sdk';
    const patch: Record<string, any> = {
      // The walkthrough asks which one they want, because a deep link from the accounts
      // page carries no mode — one launch button has to cover asking and building.
      mode: result.mode,
      nutrition_ai: nutrition,
      sdk_dev: sdk,
      target_languages: result.targetLanguages,
    };
    if (sdk) patch.sdk_platforms = result.sdkPlatform ? [result.sdkPlatform] : sdkPlatforms;
    // Never blank an existing license just because the field was left empty. The SDK uses the
    // same Passio key, so store it either way — it is the license field in both modes.
    if (result.licenseKey && (nutrition || sdk)) patch.nutrition_license_key = result.licenseKey;
    // The wizard does not ask for a model, so a build set up through it should not inherit a
    // non-builder model (e.g. an old project's Gemini) without the user choosing it.
    if (result.mode === 'app_builder' && !STUDIO_BUILD_MODELS.includes(settings.model)) {
      patch.model = STUDIO_DEFAULT_MODEL;
    }
    // Set up a fresh project rather than rewriting one that already has work in it.
    const hasWork = messages.length > 0 || Object.keys(settings.project?.files || {}).length > 0;
    const startNew = hasWork || !sessionId;
    const typed = input;
    if (startNew) {
      const id = await newChat(patch);
      if (!id) return;
    } else {
      await updateSetting(patch);
    }
    setWizardLicensePrefill('');
    if (result.starterPrompt && isStockBuilderPrompt(typed)) setInput(result.starterPrompt);
    else if (startNew) setInput(typed);
    // We just switched into App Builder, so make sure the Preview pane is actually visible.
    if (workspace === 'chat') setWorkspace('split');
    const where = startNew
      ? hasWork
        ? 'Started a new project with these settings — your previous one is still in the list on the left. '
        : 'Started a new project with these settings. '
      : 'Studio is set up. ';
    const modelNote = patch.model ? 'Using Claude Opus 5.5, the recommended builder. ' : '';
    setNotice(
      where +
        modelNote +
        (sdk
          ? `Building for the ${sdkPlatformLabel(result.sdkPlatform || '')} SDK. Native code exports — the canvas only previews a mock.`
          : nutrition && !result.licenseKey
            ? 'Add your NutritionAI license in Settings before running Preview.'
            : 'Edit the prompt in the composer and send when ready.'),
    );
  };

  const pinNutritionRoute = (route: NutritionRoute, insertMention = true) => {
    const id = nutritionRouteKey(route);
    const current: string[] = settings.nutrition_route_ids || [];
    if (!current.includes(id)) {
      void updateSetting({ nutrition_route_ids: uniqueIds([...current, id]).slice(0, 8) });
    }
    const alias = nutritionRouteAlias(route);
    if (insertMention) {
      const caret = composerRef.current?.selectionStart ?? input.length;
      const { next, caret: nextCaret } = insertNutritionMention(input, caret, alias);
      setInput(next);
      setMentionQuery(null);
      requestAnimationFrame(() => {
        const el = composerRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(nextCaret, nextCaret);
      });
    }
    setNotice(`Pinned @${alias} — next send includes this route’s request and response fields.`);
  };

  const unpinNutritionRoute = (id: string) => {
    const current: string[] = settings.nutrition_route_ids || [];
    void updateSetting({ nutrition_route_ids: current.filter((x) => x !== id) });
  };

  const pinNutritionDoc = (url: string) => {
    const current: string[] = settings.nutrition_doc_urls || [];
    if (current.includes(url)) return;
    void updateSetting({ nutrition_doc_urls: uniqueIds([...current, url]).slice(0, 6) });
    setNotice('Pinned GitBook page — next send includes that official markdown.');
  };

  const unpinNutritionDoc = (url: string) => {
    const current: string[] = settings.nutrition_doc_urls || [];
    void updateSetting({ nutrition_doc_urls: current.filter((x) => x !== url) });
  };

  const pinSdkDoc = (url: string) => {
    const current: string[] = settings.sdk_doc_urls || [];
    if (current.includes(url)) return;
    void updateSetting({ sdk_doc_urls: uniqueIds([...current, url]).slice(0, 6) });
    setNotice('Pinned SDK doc — next send includes that official markdown.');
  };

  const unpinSdkDoc = (url: string) => {
    const current: string[] = settings.sdk_doc_urls || [];
    void updateSetting({ sdk_doc_urls: current.filter((x) => x !== url) });
  };

  /**
   * Switch the header PLATFORM toggle. The three modes are mutually exclusive so neither
   * knowledge base is buried under the other's. SDK mode also seeds target_languages from
   * the chosen platform so App Builder emits the right language.
   */
  const setStudioPlatform = (next: 'easyai' | 'nutrition' | 'sdk') => {
    const patch: Record<string, any> = {
      nutrition_ai: next === 'nutrition',
      sdk_dev: next === 'sdk',
    };
    // NutritionAI is ignored outside App Builder, so picking it implies that mode.
    // SDK mode is useful in both, so it leaves the mode alone.
    if (next === 'nutrition' && !isBuilder) patch.mode = 'app_builder';
    if (next === 'sdk') {
      const langs = sdkPlatforms.flatMap((id) => SDK_PLATFORMS.find((p) => p.id === id)?.langs || []);
      if (langs.length) patch.target_languages = uniqueIds(langs);
    }
    void updateSetting(patch);
    if (next === 'nutrition' && !nutritionKeySet) {
      setSettingsTab('nutrition');
      setSettingsOpen(true);
    }
    // Without a platform the model can only ask which SDK you mean, so make the
    // choice part of turning the mode on rather than something to discover later.
    if (next === 'sdk' && sdkPlatforms.length === 0) setSdkPickerOpen(true);
  };

  const toggleSdkPlatform = (id: string) => {
    const next = sdkPlatforms.includes(id)
      ? sdkPlatforms.filter((x) => x !== id)
      : uniqueIds([...sdkPlatforms, id]);
    const langs = next.flatMap((p) => SDK_PLATFORMS.find((x) => x.id === p)?.langs || []);
    void updateSetting({
      sdk_platforms: next,
      ...(langs.length ? { target_languages: uniqueIds(langs) } : {}),
    });
  };

  /** Commit a platform from the required-choice dialog. */
  const chooseSdkPlatform = (id: string) => {
    const target = SDK_PLATFORMS.find((p) => p.id === id);
    void updateSetting({
      sdk_dev: true,
      nutrition_ai: false,
      sdk_platforms: [id],
      ...(target?.langs.length ? { target_languages: target.langs } : {}),
    });
    setSdkPickerOpen(false);
    setNotice(`${target?.label || 'SDK'} knowledge loaded. Ask anything, or describe what to build.`);
  };

  /** Backing out of the required picker leaves SDK mode rather than a half-set session. */
  const cancelSdkPicker = () => {
    setSdkPickerOpen(false);
    if (sdkPlatforms.length === 0 && sdkOn) {
      void updateSetting({ sdk_dev: false });
      setNotice('Left Mobile SDK mode — no platform was chosen.');
    }
  };

  const pinSdkCapability = (route: NutritionRoute, alsoMention = true) => {
    const id = nutritionRouteKey(route);
    const current: string[] = settings.sdk_capability_ids || [];
    if (!current.includes(id)) {
      void updateSetting({ sdk_capability_ids: uniqueIds([...current, id]).slice(0, 8) });
    }
    if (alsoMention) {
      const alias = nutritionRouteAlias(route);
      setInput((prev) => insertNutritionMention(prev, prev.length, alias).next);
    }
    setNotice(
      route.sdk_covered
        ? `Pinned ${route.sdk_method} — the next send knows that call and its data shape.`
        : `Pinned ${route.summary} — no SDK method for it, so the model will wire a REST call.`,
    );
  };

  const unpinSdkCapability = (id: string) => {
    const current: string[] = settings.sdk_capability_ids || [];
    void updateSetting({ sdk_capability_ids: current.filter((x) => x !== id) });
  };

  /**
   * Pin a route picked from the composer's `@` list. The two platforms keep separate pin
   * lists and separate prompt sections, so the mention has to land in the one matching the
   * active platform — pinning an SDK capability as a REST route would inject the wrong
   * section and lose the method name.
   */
  const pinMentionedRoute = (route: NutritionRoute) => {
    if (sdkOn) pinSdkCapability(route, true);
    else pinNutritionRoute(route, true);
  };

  const recordStudioStep = async (payload: {
    kind: string;
    path?: string;
    prompt?: string;
    selector?: string;
    url?: string;
    summary?: string;
  }) => {
    if (!sessionId) return;
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/v2/playground/sessions/${sessionId}/project/step`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.project) applyProject(data.project);
      if (data.message) setMessages((prev) => [...prev, data.message]);
    } catch {
      // canvas action already applied; history is best-effort
    }
  };

  const applyProject = (proj: ProjectState) => {
    setSettings((prev) => ({ ...prev, mode: 'app_builder', project: { ...prev.project, ...proj } }));
  };

  const saveProjectFile = async (path: string, content: string) => {
    if (!sessionId) return;
    const headers = await authHeaders();
    const res = await fetch(`/api/v2/playground/sessions/${sessionId}/project/files`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ path, content }),
    });
    if (res.ok) {
      const data = await res.json();
      applyProject(data.project);
    }
  };

  const deleteProjectFile = async (path: string) => {
    if (!sessionId) return;
    const headers = await authHeaders();
    const res = await fetch(
      `/api/v2/playground/sessions/${sessionId}/project/files?path=${encodeURIComponent(path)}`,
      { method: 'DELETE', headers },
    );
    if (res.ok) {
      const data = await res.json();
      applyProject(data.project);
    }
  };

  const renameProjectFile = async (from: string, to: string) => {
    const rec = project.files?.[from];
    if (!rec) return;
    await saveProjectFile(to, rec.content || '');
    await deleteProjectFile(from);
  };

  const saveNotes = async (notes: string) => {
    const trimmed = (notes || '').trim();
    if (!trimmed) return;
    const log = [...(project.notes_log || [])];
    const last = log[log.length - 1];
    const text = trimmed.slice(0, 8000);
    if (!(last && last.source === 'user' && String(last.text || '') === text)) {
      log.push({
        at: new Date().toISOString(),
        source: 'user',
        text,
      });
    }
    const next = { ...settings, project: { ...project, notes: '', notes_log: log.slice(-40) } };
    setSettings(next);
    await persistSettings(next);
  };

  const generateProjectImage = async (
    path: string,
    prompt: string,
    model?: string,
    selector?: string,
  ): Promise<string | void> => {
    if (!sessionId) return;
    setGeneratingAsset(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/v2/playground/sessions/${sessionId}/project/generate-image`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ path, prompt, model: model || undefined, selector: selector || undefined }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      applyProject(data.project);
      return data.url;
    } catch (e: any) {
      setError(e.message || 'Image generation failed');
    } finally {
      setGeneratingAsset(false);
    }
  };

  const generateProjectVideo = async (
    path: string,
    prompt: string,
    model?: string,
    selector?: string,
  ): Promise<string | void> => {
    if (!sessionId) return;
    setGeneratingAsset(true);
    setError(null);
    setNotice('Generating a short video — this can take a minute…');
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/v2/playground/sessions/${sessionId}/project/generate-video`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ path, prompt, model: model || undefined, selector: selector || undefined }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      applyProject(data.project);
      setNotice(null);
      return data.url;
    } catch (e: any) {
      setError(e.message || 'Video generation failed');
      setNotice(null);
    } finally {
      setGeneratingAsset(false);
    }
  };

  const uploadProjectAsset = async (file: File): Promise<{ path: string; url: string } | void> => {
    if (!sessionId) return;
    setError(null);
    try {
      const token = await getIdToken();
      const form = new FormData();
      form.append('file', file);
      const up = await fetch(`/api/v2/playground/sessions/${sessionId}/attachments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!up.ok) throw new Error(await up.text());
      const att = await up.json();
      const path = `assets/${(file.name || 'asset').replace(/[^\w.-]+/g, '_')}`;
      const headers = await authHeaders();
      const kind = (file.type || '').startsWith('video/') ? 'video' : 'image';
      const res = await fetch(`/api/v2/playground/sessions/${sessionId}/project/files`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ path, content: '', url: att.url, language: kind }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      applyProject(data.project);
      return { path, url: att.url };
    } catch (e: any) {
      setError(e.message || 'Upload failed');
    }
  };

  const previewChain = async (payload: {
    action: 'execute' | 'poll';
    chainId?: string;
    executionId?: string;
    body?: string;
  }): Promise<{ status: number; body: any; error?: string }> => {
    if (!sessionId) return { status: 400, body: { detail: 'No session' }, error: 'No session' };
    let input_data: Record<string, unknown> = {};
    if (payload.body) {
      try {
        const parsed = JSON.parse(payload.body);
        input_data = parsed.input_data || parsed;
      } catch {
        input_data = {};
      }
    }
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/v2/playground/sessions/${sessionId}/preview/chain`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: payload.action,
          chain_id: payload.chainId,
          execution_id: payload.executionId,
          input_data,
        }),
      });
      const body = await res.json().catch(() => ({ detail: 'Bad response' }));
      const err =
        !res.ok
          ? typeof body.detail === 'string'
            ? body.detail
            : JSON.stringify(body.detail || body)
          : undefined;
      return { status: res.status, body, error: err };
    } catch (e: any) {
      return { status: 502, body: { detail: e.message }, error: e.message };
    }
  };

  const previewNutrition = async (payload: {
    method: string;
    path: string;
    query?: Record<string, string>;
    body?: unknown;
  }): Promise<{ status: number; body: any; error?: string; requestId?: string }> => {
    if (!sessionId) return { status: 400, body: { detail: 'No session' }, error: 'No session' };
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/v2/playground/sessions/${sessionId}/preview/nutrition`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          method: payload.method,
          path: payload.path,
          query: payload.query || {},
          body: payload.body,
        }),
      });
      const body = await res.json().catch(() => ({ detail: 'Bad response' }));
      const requestId = res.headers.get('X-Nutrition-Request-ID') || undefined;
      const err = !res.ok
        ? typeof body.detail === 'string'
          ? body.detail
          : JSON.stringify(body.detail || body)
        : undefined;
      return { status: res.status, body: !res.ok && body.detail != null ? body.detail : body, error: err, requestId };
    } catch (e: any) {
      return { status: 502, body: { detail: e.message }, error: e.message };
    }
  };

  const previewFunction = async (payload: {
    name: string;
    customerId?: string;
    body?: string;
  }): Promise<{ status: number; body: any; error?: string; runId?: string }> => {
    if (!sessionId) return { status: 400, body: { detail: 'No session' }, error: 'No session' };
    let parsed: Record<string, unknown> = {};
    if (payload.body) {
      try {
        parsed = JSON.parse(payload.body);
      } catch {
        parsed = { input: payload.body };
      }
    }
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/v2/playground/sessions/${sessionId}/preview/function`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: payload.name,
          input: parsed.input ?? parsed,
          input_type: parsed.input_type || 'text',
          model: parsed.model,
        }),
      });
      const body = await res.json().catch(() => ({ detail: 'Bad response' }));
      const runId = res.headers.get('EASYAI-RUN-ID') || undefined;
      const err = !res.ok
        ? typeof body.detail === 'string'
          ? body.detail
          : JSON.stringify(body.detail || body)
        : undefined;
      return { status: res.status, body, error: err, runId };
    } catch (e: any) {
      return { status: 502, body: { detail: e.message }, error: e.message };
    }
  };

  const previewFeedback = async (payload: { batch: boolean; body?: string }): Promise<{ status: number; body: any; error?: string }> => {
    if (!sessionId) return { status: 400, body: { detail: 'No session' }, error: 'No session' };
    let parsed: any = {};
    try {
      parsed = payload.body ? JSON.parse(payload.body) : {};
    } catch {
      return { status: 422, body: { detail: 'Feedback body must be JSON' }, error: 'Feedback body must be JSON' };
    }
    const items = payload.batch ? parsed.items : [parsed];
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/v2/playground/sessions/${sessionId}/preview/feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ items }),
      });
      const body = await res.json().catch(() => ({ detail: 'Bad response' }));
      if (!res.ok) {
        const err = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body);
        return { status: res.status, body, error: err };
      }
      if (payload.batch) return { status: res.status, body };
      if (body.created?.length) return { status: 201, body: body.created[0] };
      if (body.duplicates?.length) return { status: 201, body: { duplicate: true, ...body.duplicates[0] } };
      const err = body.rejected?.[0]?.error || 'Feedback rejected';
      return { status: 404, body: { detail: err }, error: err };
    } catch (e: any) {
      return { status: 502, body: { detail: e.message }, error: e.message };
    }
  };

  const askImportFolder = async (list: FileList) => {
    if (Object.keys(project.files || {}).length > 0) {
      setImportAsk(list);
      return;
    }
    await importFolder(list);
  };

  const importFolder = async (list: FileList) => {
    if (!sessionId) return;
    const picked = Array.from(list).filter((f) => {
      const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
      if (SKIP_IMPORT.test(rel)) return false;
      if (f.size > 250_000 && !f.type.startsWith('image/')) return false;
      return TEXT_IMPORT.test(rel) || f.type.startsWith('image/') || f.type.startsWith('text/');
    }).slice(0, 80);
    if (!picked.length) {
      setError('No usable source files in that folder (skipped node_modules, binaries, …).');
      return;
    }
    setNotice(`Importing ${picked.length} files…`);
    const files: { path: string; content?: string; url?: string; language?: string }[] = [];
    for (const file of picked) {
      const rel = ((file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name)
        .replace(/^[^/]+\//, '');
      if (!rel) continue;
      if (file.type.startsWith('image/')) {
        const rec = await uploadProjectAsset(file);
        if (rec?.url) files.push({ path: rel.startsWith('assets/') ? rel : `assets/${file.name}`, url: rec.url, content: '', language: 'image' });
        continue;
      }
      try {
        const content = await file.text();
        files.push({ path: rel.slice(0, 180), content, language: undefined });
      } catch {
        // skip
      }
    }
    if (!files.length) {
      setError('Could not read those files.');
      setNotice(null);
      return;
    }
    const headers = await authHeaders();
    const res = await fetch(`/api/v2/playground/sessions/${sessionId}/project`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        files,
        preview_path: files.find((f) => f.path.endsWith('.html'))?.path || 'index.html',
        imported: true,
      }),
    });
    if (!res.ok) {
      setError(await res.text());
      setNotice(null);
      return;
    }
    applyProject((await res.json()).project);
    setNotice(`Imported ${files.length} files. Describe what to add and where — we’ll match the existing layout.`);
    await recordStudioStep({
      kind: 'import',
      summary: `Imported ${files.length} files (${files.map((f) => f.path).slice(0, 12).join(', ')}${files.length > 12 ? '…' : ''}).`,
    });
    if (isStockBuilderPrompt(input)) {
      setInput(
        'Look at the imported project. Add the attached chain/function as a new page or section that matches existing styles, routing, and env vars. Do not rewrite unrelated files.',
      );
    }
  };

  const downloadZip = async () => {
    if (!sessionId) return;
    const token = await getIdToken();
    const res = await fetch(`/api/v2/playground/sessions/${sessionId}/project.zip`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      setError('Could not download zip');
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadFilename(res, 'studio-app.zip');
    a.click();
    URL.revokeObjectURL(url);
  };

  const renameChat = async (id: string, title: string) => {
    const trimmed = title.trim();
    setEditingTitleId(null);
    if (!trimmed) return;
    const current = sessions.find((s) => s.id === id);
    if (current && current.title === trimmed) return;
    const headers = await authHeaders();
    const res = await fetch(`/api/v2/playground/sessions/${id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ title: trimmed }),
    });
    if (!res.ok) {
      setError('Could not rename chat');
      return;
    }
    const updated: Session = await res.json();
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, title: updated.title } : s)));
  };

  const exportLocalPack = async (hostTarget = 'auto') => {
    if (!sessionId) return;
    setExporting(true);
    setError(null);
    setNotice('Writing README, start scripts, hosting notes, and deploy files for your chosen host…');
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/v2/playground/sessions/${sessionId}/export-pack`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          model: isImageModel ? DEFAULT_SETTINGS.model : settings.model,
          host_target: hostTarget,
        }),
      });
      if (!res.ok) throw new Error((await res.text()) || 'Export pack failed');
      const data = await res.json();
      applyProject(data.project);
      const names = (data.files_added || []).join(', ');
      setNotice(
        names
          ? `Added ${names}. Downloading zip — unzip and open HOSTING.md (or run scripts/dev.sh).`
          : 'Pack ready. Downloading zip.',
      );
      await downloadZip();
    } catch (e: any) {
      setError(e.message || 'Export pack failed');
    } finally {
      setExporting(false);
    }
  };

  const copyCursorBrief = async () => {
    const text = `In Cursor: File → Open Folder on the unzipped Cursor pack.

Read START_HERE.md first, then AGENTS.md. Call attached EasyAI chains — do not recreate them.
Keep demoMode true until you paste YOUR_EASYAI_API_KEY. Do not commit secrets.`;
    try {
      await navigator.clipboard.writeText(text);
      setNotice('Copied the Cursor one-liner. Download the Cursor pack, unzip, File → Open Folder, read START_HERE.md.');
    } catch {
      setError('Could not copy to clipboard');
    }
  };

  const exportCursorPack = async () => {
    if (!sessionId) return;
    setExportingCursor(true);
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`/api/v2/playground/sessions/${sessionId}/cursor-pack.zip`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.text()) || 'Could not build Cursor pack');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename(res, 'studio-app-cursor-pack.zip');
      a.click();
      URL.revokeObjectURL(url);
      setNotice('Downloaded Cursor pack. Unzip → Cursor File → Open Folder → read START_HERE.md.');
    } catch (e: any) {
      setError(e.message || 'Could not download Cursor pack');
      throw e;
    } finally {
      setExportingCursor(false);
    }
  };

  const askRestore = async (messageId: string) => {
    if (Object.keys(project.files || {}).length === 0) {
      await restoreFromMessage(messageId);
      return;
    }
    setRestoreAsk(messageId);
  };

  const restoreFromMessage = async (messageId: string) => {
    if (!sessionId) return;
    setRestoring(messageId);
    setError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/v2/playground/sessions/${sessionId}/restore`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message_id: messageId }),
      });
      if (res.ok) {
        const data = await res.json();
        applyProject(data.project);
        setNotice('Restored app files from that reply. Chat history is unchanged.');
        return;
      }
      const local = messages.find((m) => m.id === messageId);
      const snap = local?.settings_snapshot?.project;
      const extracted = extractProjectFilesLite(local?.content || '');
      const files = snap?.files && Object.keys(snap.files).length
        ? Object.values(snap.files)
        : extracted;
      if (!files.length) throw new Error(await res.text());
      const put = await fetch(`/api/v2/playground/sessions/${sessionId}/project`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ files, preview_path: snap?.preview_path || 'index.html' }),
      });
      if (!put.ok) throw new Error(await put.text());
      applyProject((await put.json()).project);
      setNotice('Restored app files from that reply. Chat history is unchanged.');
    } catch (e: any) {
      setError(e.message || 'Restore failed');
    } finally {
      setRestoring(null);
    }
  };

  const onModelChange = async (v: string | string[]) => {
    const model = Array.isArray(v) ? v[0] : v;
    await updateSetting({ model });
  };

  const flushAssistant = async (
    sid: string,
    messageId: string,
    content: string,
    arts: { kind?: string; url?: string }[],
  ) => {
    if (!UUID_RE.test(messageId) || !content) return;
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/v2/playground/sessions/${sid}/messages/${messageId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ content, artifacts: arts }),
      });
      if (res.ok) {
        const saved = await res.json();
        setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, ...saved } : m)));
      }
    } catch {
      // keep local copy
    }
  };

  const ingestExtracted = async (id: string, text: string): Promise<boolean> => {
    const files = extractProjectFilesLite(text);
    if (!files.length) return false;
    const headers = await authHeaders();
    const res = await fetch(`/api/v2/playground/sessions/${id}/project`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ files, preview_path: 'index.html' }),
    });
    if (!res.ok) return false;
    applyProject((await res.json()).project);
    return true;
  };

  const attachHit = async (hit: CatalogHit, opts?: { fillPrompt?: boolean }) => {
    const key =
      hit.type === 'chain'
        ? 'chain_ids'
        : hit.type === 'object'
          ? 'object_ids'
          : hit.type === 'function'
            ? 'function_ids'
            : 'category_ids';
    const current: string[] = settings[key] || [];
    if (current.includes(hit.id)) {
      setNotice(hit.type === 'chain' ? 'That chain is already attached.' : 'Already attached.');
      return;
    }
    const next = withUniqueAttach({ ...settings, [key]: [...current, hit.id] });
    setSettings(next);
    await persistSettings(next);
    setNotice(
      hit.type === 'chain' || hit.type === 'function'
        ? `Attached ${hit.name}${formatWhen(hit.updated_at) ? ` · ${formatWhen(hit.updated_at)}` : ''}.`
        : ATTACH_HELP[hit.type] || 'Attached for the next message.',
    );
    if (hit.type === 'chain') {
      const extend = projectLooksImported(project);
      if (opts?.fillPrompt !== false && isStockBuilderPrompt(input)) setInput(chainBuildPrompt(hit.name, extend));
      if (sessionId && !isBuilder) {
        const headers = await authHeaders();
        const res = await fetch(`/api/v2/playground/sessions/${sessionId}/chain-builder`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ chain_id: hit.id }),
        });
        if (res.ok) {
          const updated = await res.json();
          setSettings(withUniqueAttach({ ...DEFAULT_SETTINGS, ...(updated.settings || next) }));
        }
      }
    }
    if (hit.type === 'function' && opts?.fillPrompt !== false && isStockBuilderPrompt(input)) {
      setInput(
        projectLooksImported(project)
          ? `Add a page that calls the attached function “${hit.name}” into this imported app. Match existing layout. data-page=input|waiting|results.`
          : `Build a complete web app that calls the attached function “${hit.name}” (replace any current preview — this is a new app). Form for its input, wait UI, then show the JSON result. PASSIO_CONFIG.easyAiKey + easyAiBase. Always preventDefault on the form and use fetch. data-page=input|waiting|results.`,
      );
    }
  };

  const removeAttach = async (key: string, id: string) => {
    const current: string[] = settings[key] || [];
    const next = withUniqueAttach({ ...settings, [key]: current.filter((x) => x !== id) });
    if (key === 'chain_ids' && next.chain_builder_context?.id === id) {
      next.chain_builder_context = null;
    }
    setSettings(next);
    await persistSettings(next);
  };

  const insertComposerMention = (alias: string) => {
    const caret = composerRef.current?.selectionStart ?? input.length;
    const { next, caret: nextCaret } = insertNutritionMention(input, caret, alias);
    setInput(next);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      const el = composerRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const pinChain = (hit: CatalogHit, source: 'mention' | 'dialog') => {
    const fill = source === 'dialog' && isStockBuilderPrompt(input);
    void attachHit(hit, { fillPrompt: fill });
    if (!fill) insertComposerMention(chainMentionSlug(hit.name));
    else setMentionQuery(null);
    if (source === 'dialog') setChainPickerOpen(false);
  };

  const uploadFiles = async (files: FileList) => {
    let id = sessionId;
    if (!id) id = await newChat();
    if (!id) return;
    const token = await getIdToken();
    const uploaded: Attachment[] = [];
    for (const file of Array.from(files)) {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`/api/v2/playground/sessions/${id}/attachments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (res.ok) uploaded.push(await res.json());
    }
    setPendingFiles((prev) => [...prev, ...uploaded]);
  };

  const uploadDataUrl = async (dataUrl: string, filename: string): Promise<Attachment | null> => {
    let id = sessionId;
    if (!id) id = await newChat();
    if (!id) return null;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], filename, { type: blob.type || 'image/png' });
      const token = await getIdToken();
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`/api/v2/playground/sessions/${id}/attachments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  };

  const runCompare = async (opts: { content?: string; userMessageId?: string; attachments?: Attachment[] }) => {
    const models = Array.from(new Set((compareModels.length ? compareModels : [settings.model]).filter(Boolean))).slice(0, 4);
    if (models.length < 2) {
      setCompareOpen(true);
      setError('Pick at least two different text models to compare.');
      return;
    }
    let id = sessionId;
    if (!id) id = await newChat();
    if (!id) return;
    pinToBottom.current = true;
    sendingRef.current = true;
    setSending(true);
    setError(null);
    setStreamingText('');
    setGeneratingImage(false);
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    let groupId = opts.userMessageId || '';
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/v2/playground/sessions/${id}/compare`, {
        method: 'POST',
        headers: { ...headers, Accept: 'text/event-stream' },
        signal,
        body: JSON.stringify({
          models,
          content: opts.userMessageId ? undefined : opts.content,
          user_message_id: opts.userMessageId,
          attachments: opts.attachments || [],
          temperature: settings.temperature,
        }),
      });
      if (!res.ok || !res.body) throw new Error((await res.text()) || `HTTP ${res.status}`);
      await readSseResponse(res, (event: any) => {
        if (event.type === 'compare_start') {
          groupId = event.group_id;
          if (!opts.userMessageId) {
            const content = event.content || opts.content || '';
            setMessages((m) => {
              if (m.some((x) => x.id === groupId)) return m;
              return [...m, { id: groupId, role: 'user', content, attachments: opts.attachments }];
            });
            setInput('');
            setPendingFiles([]);
          }
        } else if (event.type === 'lane') {
          setCompareStream({ groupId, model: event.model, text: '' });
          setCompareSel((s) => ({ ...s, [groupId]: '__stream__' }));
        } else if (event.type === 'delta') {
          setCompareStream((s) =>
            s ? { ...s, text: s.text + (event.text || '') } : { groupId, model: event.model || '', text: event.text || '' },
          );
        } else if (event.type === 'persisted' && event.message_id) {
          const text = event.text || '';
          setMessages((m) => {
            if (m.some((x) => x.id === event.message_id)) {
              return m.map((x) =>
                x.id === event.message_id
                  ? { ...x, content: text, model_used: event.model, usage: event.credits_used != null ? { credits_used: event.credits_used } : x.usage }
                  : x,
              );
            }
            return [
              ...m,
              {
                id: event.message_id,
                role: 'assistant' as const,
                content: text,
                model_used: event.model,
                usage: event.credits_used != null ? { credits_used: event.credits_used } : undefined,
                settings_snapshot: { compare_group: groupId, model: event.model },
              },
            ];
          });
          setCompareSel((s) => ({ ...s, [groupId]: event.message_id }));
          setCompareStream(null);
        } else if (event.type === 'error' && event.message) {
          setNotice(event.message);
        }
      });
      setReplayUserId(null);
      loadSessions().catch(() => undefined);
    } catch (e: any) {
      setError(e.message || 'Compare failed');
    } finally {
      setCompareStream(null);
      finishSending();
    }
  };

  const send = async (overrideText?: string, extraAttachments?: Attachment[], replaceAttachments = false) => {
    const text = (overrideText ?? input).trim();
    const mergedAttachments = replaceAttachments
      ? extraAttachments || []
      : extraAttachments?.length
        ? [...pendingFiles, ...extraAttachments]
        : [...pendingFiles];
    if (sendingRef.current) {
      if (replayUserId && compareOpen) return;
      if (compareOpen && !isImageModel && compareModels.filter(Boolean).length >= 2) return;
      if (!text && !mergedAttachments.length) return;
      const replacing = !!queuedRef.current;
      queuedRef.current = { text, attachments: mergedAttachments };
      setQueued(queuedRef.current);
      setNotice(
        replacing
          ? 'Queued (replaced) — sends when this reply finishes.'
          : 'Queued — sends when this reply finishes.',
      );
      if (overrideText === undefined) setInput('');
      setPendingFiles([]);
      return;
    }
    if (replayUserId && compareOpen) {
      await runCompare({ userMessageId: replayUserId });
      return;
    }
    if (!text) {
      const q = queuedRef.current;
      if (q && !compareOpen) {
        queuedRef.current = null;
        setQueued(null);
        await send(q.text, q.attachments, true);
      }
      return;
    }
    // A send in SDK mode with no platform can only produce "which SDK are you using?",
    // so spend the turn on the answer instead of the question.
    if (sdkOn && sdkPlatforms.length === 0) {
      setSdkPickerOpen(true);
      setNotice('Pick your SDK first — then send. Otherwise the answer cannot be platform-specific.');
      return;
    }
    if (compareOpen && !isImageModel && compareModels.filter(Boolean).length >= 2) {
      await runCompare({ content: text, attachments: mergedAttachments });
      return;
    }
    let id = sessionId;
    if (!id) id = await newChat();
    if (!id) return;
    pinToBottom.current = true;
    sendingRef.current = true;
    setSending(true);
    setError(null);
    setStreamingText('');
    setThinkingText('');
    setStreamStartedAt(Date.now());
    setGeneratingImage(false);
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    const attachments = mergedAttachments;
    const userMsg: Message = {
      id: `local-${Date.now()}`,
      role: 'user',
      content: text,
      attachments,
    };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setPendingFiles([]);

    let acc = '';
    let thinkingAcc = '';
    let truncated = false;
    let artifacts: { kind?: string; url?: string }[] = [];
    let streamErr: string | null = null;
    let assistantId = `asst-${Date.now()}`;
    let gotProject = false;
    let creditsThisTurn: number | undefined;
    let projectSnap: ProjectState | undefined;

    const onEvent = (event: any) => {
      if (event.type === 'delta') {
        acc += event.text || '';
        setStreamingText(acc);
      } else if (event.type === 'thinking') {
        thinkingAcc = (thinkingAcc + (event.text || '')).slice(-4000);
        setThinkingText(thinkingAcc);
      } else if (event.type === 'generating') {
        setGeneratingImage(true);
      } else if (event.type === 'image') {
        artifacts.push({ kind: 'image', url: event.url });
        setGeneratingImage(false);
      } else if (event.type === 'done') {
        acc = event.text || acc;
        artifacts = event.artifacts || artifacts;
        if (event.truncated) truncated = true;
        setStreamingText(acc);
      } else if ((event.type === 'assistant_id' || event.type === 'persisted') && event.message_id) {
        assistantId = event.message_id;
        if (event.credits_used != null) creditsThisTurn = event.credits_used;
        if (event.user_message_id) {
          const uid = event.user_message_id;
          setMessages((m) => m.map((x) => (x.id === userMsg.id ? { ...x, id: uid } : x)));
        }
      } else if (event.type === 'context_selection') {
        const total: number = event.total || 0;
        const outlined: number = (event.outlined || []).length;
        const picked: number = (event.picked || []).length;
        const named: number = (event.mentioned || []).length;
        const small: number = (event.small || []).length;
        const extras = [
          small ? `${small} small file${small === 1 ? '' : 's'}` : '',
          named ? `${named} you named` : '',
        ].filter(Boolean);
        setNotice(
          event.mode === 'ai'
            ? `Smart file selection: sent ${total - outlined} of ${total} files in full — AI picked ${picked}` +
                (extras.length ? `, plus ${extras.join(' and ')}` : '') +
                `. ${outlined} sent as outlines.`
            : 'Smart file selection could not decide this turn, so files were sent as usual.',
        );
      } else if (event.type === 'attached_chain' && event.id) {
        setSettings((prev) => {
          const ids: string[] = prev.chain_ids || [];
          if (ids.includes(event.id)) return prev;
          return { ...prev, chain_ids: [...ids, event.id] };
        });
        setCatalogHits((prev) =>
          prev.some((h) => h.id === event.id)
            ? prev
            : [{ type: 'chain', id: event.id, name: event.name, updated_at: new Date().toISOString() }, ...prev],
        );
        setNotice(`Using chain “${event.name}” (latest matching). The app will call it with your EasyAI API key.`);
      } else if (event.type === 'project' && event.project) {
        gotProject = true;
        applyProject(event.project);
        projectSnap = event.project;
        if (event.message_id) assistantId = event.message_id;
        const skipped: string[] = event.skipped_files || [];
        if (skipped.length) {
          setNotice(
            `Not applied: ${skipped.join(', ')}. The AI only saw an outline of ${skipped.length > 1 ? 'these files' : 'this file'}, so its version would drop code. Mention the file name in your next message to send it in full.`,
          );
        }
        if (event.title && sessionId) {
          const titled = event.title;
          setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, title: titled } : s)));
        }
        setCanvasHint(null);
      } else if (event.type === 'error') {
        streamErr = event.message || 'Model error';
      }
    };

    const commitAssistant = () => {
      if (!acc && artifacts.length === 0) return;
      setMessages((m) => [
        ...m,
        {
          id: assistantId,
          role: 'assistant',
          content: acc,
          artifacts,
          model_used: settings.model,
          usage: creditsThisTurn != null ? { credits_used: creditsThisTurn } : undefined,
          settings_snapshot: projectSnap ? { project: projectSnap } : undefined,
        },
      ]);
    };

    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/v2/playground/sessions/${id}/messages`, {
        method: 'POST',
        headers: { ...headers, Accept: 'text/event-stream' },
        signal,
        body: JSON.stringify({
          content: text,
          attachments,
          model: settings.model,
          temperature: settings.temperature,
          persist_settings: true,
          stream: true,
        }),
      });
      if (!res.ok || !res.body) {
        const err = await res.text();
        throw new Error(err || `HTTP ${res.status}`);
      }
      await readSseResponse(res, onEvent);
      setStreamingText('');
      setGeneratingImage(false);
      commitAssistant();
      await flushAssistant(id, assistantId, acc, artifacts);
      if (streamErr && !acc && artifacts.length === 0) setError(streamErr);
      else if (streamErr) setNotice(`Reply kept. Connection note: ${streamErr}`);
      if (isBuilder && !gotProject && acc) {
        const loaded = await ingestExtracted(id, acc);
        if (loaded) {
          setNotice('Loaded files from that reply into the preview.');
          setCanvasHint(null);
        } else if (!streamErr) {
          setNotice('Reply saved. If the preview is empty, ask it to re-emit files as ```html path=index.html fences.');
          if (builderReplyMissedCanvas(acc)) {
            setCanvasHint(
              'This reply did not load into Preview. Ask chat to re-emit each file as a markdown fence with path= on the opening line, e.g. ```html path=index.html',
            );
          }
        }
      }
      if (truncated) {
        setError(
          isBuilder
            ? 'The reply hit the model’s output limit, so the last file was cut off and is not in the preview. ' +
                'Send “Continue: re-emit any unfinished files in full, then the rest” to finish it. ' +
                'Fewer target languages (just HTML/CSS/JS) leaves more room.'
            : 'The reply hit the model’s output limit and was cut off. Send “Continue” to get the rest.',
        );
      }
      loadSessions().catch(() => undefined);
    } catch (e: any) {
      setStreamingText('');
      setGeneratingImage(false);
      if (acc || artifacts.length) {
        commitAssistant();
        await flushAssistant(id, assistantId, acc, artifacts);
        if (isBuilder && !gotProject && acc) {
          const loaded = await ingestExtracted(id, acc);
          if (loaded) {
            setNotice('Loaded files from that reply into the preview.');
            setCanvasHint(null);
          } else if (!streamErr) {
            setNotice('Reply saved. If the preview is empty, ask it to re-emit files as ```html path=index.html fences.');
            if (builderReplyMissedCanvas(acc)) {
              setCanvasHint(
                'This reply did not load into Preview. Ask chat to re-emit each file as a markdown fence with path= on the opening line, e.g. ```html path=index.html',
              );
            }
          }
        }
        // A finished SSE often throws "network error" after the last event.
        // Do not show "Connection dropped" when we already have the reply.
      } else if (e?.name !== 'AbortError') {
        setError(e.message || 'Send failed');
      }
      loadSessions().catch(() => undefined);
    } finally {
      finishSending();
    }
  };
  sendRef.current = send;

  const handleStudioTarget = async (payload: {
    text: string;
    notes: string;
    imageDataUrl?: string | null;
    attachments?: { url: string; filename?: string; kind?: string; content_type?: string }[];
  }) => {
    setWorkspace('split');
    let extra: Attachment[] = (payload.attachments || []).map((a) => ({
      url: a.url,
      filename: a.filename,
      kind: a.kind,
      content_type: a.content_type,
    }));
    if (payload.imageDataUrl) {
      const att = await uploadDataUrl(payload.imageDataUrl, 'studio-selection.png');
      if (att) extra = [att, ...extra];
    }
    const body = (payload.text || '').trim();
    if (!body) return;
    await send(body, extra);
  };

  /**
   * Which platform tab this session belongs to. Drives the tab Settings lands on so
   * the knobs for the mode you are actually in are the ones in front of you.
   */
  const activePlatformTab: SettingsTab = sdkOn ? 'sdk' : nutritionOn ? 'nutrition' : 'easyai';

  const openSettings = (tab?: SettingsTab) => {
    setSettingsTab(tab || activePlatformTab);
    setSettingsOpen(true);
  };

  const namedAttach = useCallback(
    (ids: string[], type: string) => {
      return uniqueIds(ids).map((id) => {
        const hit = catalogHits.find((h) => h.id === id && h.type === type);
        return {
          id,
          name: hit?.name || id.slice(0, 8),
          date: formatWhen(hit?.updated_at),
        };
      });
    },
    [catalogHits],
  );

  const modelLabel = MODEL_REGISTRY[settings.model]?.displayName || settings.model;
  const sessionCredits = useMemo(
    () => messages.reduce((sum, m) => sum + (Number(m.usage?.credits_used) || 0), 0),
    [messages],
  );
  const chatBlocks = useMemo(() => blocksFromMessages(messages), [messages]);

  const searchHits = useMemo(() => {
    const q = catalogQ.trim().toLowerCase();
    const list = catalogHits.filter((h) => h.type === attachKind);
    const filtered = q
      ? list.filter(
          (h) =>
            h.name.toLowerCase().includes(q) ||
            (h.description || '').toLowerCase().includes(q),
        )
      : list;
    return filtered.sort(byUpdatedDesc).slice(0, 14);
  }, [catalogHits, catalogQ, attachKind]);

  useEffect(() => {
    if (!chainPickerOpen) return;
    void loadCatalog('');
  }, [chainPickerOpen, loadCatalog]);

  useEffect(() => {
    if (!settingsOpen) return;
    setCatalogOpen(true);
    setAttachKind('chain');
    loadCatalog('');
  }, [settingsOpen, loadCatalog]);

  // A stale tab is worse than no tab: if the platform changed while Settings was
  // closed, land on the one that matches the session.
  useEffect(() => {
    if (settingsOpen) return;
    setSettingsTab(activePlatformTab);
  }, [settingsOpen, activePlatformTab]);

  if (loading) {
    return (
      <ThemeProvider theme={playgroundMuiTheme}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8, bgcolor: PG.bg, height: '100%' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={playgroundMuiTheme}>
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        flex: 1,
        minHeight: 0,
        gap: 1.5,
        bgcolor: PG.bg,
        color: PG.text,
        ...(expanded
          ? {
              position: 'fixed',
              inset: 0,
              zIndex: 1400,
              bgcolor: '#0b1220',
              p: 1.5,
            }
          : {}),
      }}
    >
      <Paper variant="outlined" sx={{ width: 248, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: PG.panel, borderColor: PG.border, color: PG.text }}>
        <Box sx={{ p: 1.5 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => newChat()}
            sx={{
              color: PG.text,
              borderColor: PG.border,
              bgcolor: 'rgba(56,189,248,0.1)',
              ...pgBtn,
              fontWeight: 700,
              py: 0.85,
              '&:hover': { borderColor: PG.accent, bgcolor: 'rgba(56,189,248,0.2)' },
            }}
          >
            {isBuilder ? 'New app' : 'New chat'}
          </Button>
          {sessionCredits > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: PG.muted, textAlign: 'center' }}>
              This chat · {formatCredits(sessionCredits)}
            </Typography>
          )}
        </Box>
        <Divider />
        <List sx={{ overflow: 'auto', flex: 1, py: 0 }}>
          {sessions.map((s) => (
            <ListItemButton
              key={s.id}
              selected={s.id === sessionId}
              onClick={() => {
                if (editingTitleId !== s.id) loadSession(s.id);
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                setEditingTitleId(s.id);
                setTitleDraft(s.title || '');
              }}
              sx={{ py: 1, px: 1.25, alignItems: 'flex-start' }}
            >
              {editingTitleId === s.id ? (
                <TextField
                  autoFocus
                  size="small"
                  fullWidth
                  value={titleDraft}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={() => renameChat(s.id, titleDraft)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      renameChat(s.id, titleDraft);
                    }
                    if (e.key === 'Escape') setEditingTitleId(null);
                  }}
                  sx={{ mr: 0.5, '& .MuiInputBase-input': { fontSize: '0.85rem', color: PG.text } }}
                />
              ) : (
                <ListItemText
                  primary={s.title || 'New chat'}
                  primaryTypographyProps={{ noWrap: true, fontSize: '0.85rem', color: PG.text }}
                  secondary={`${s.message_count || 0} · ${formatShortDate(s.updated_at) || '—'}`}
                  secondaryTypographyProps={{ fontSize: '0.7rem', color: PG.muted }}
                />
              )}
              <Tooltip title="Rename">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingTitleId(s.id);
                    setTitleDraft(s.title || '');
                  }}
                  sx={{ color: PG.muted }}
                >
                  <DriveFileRenameOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={s.settings?.mode === 'app_builder' ? 'Delete this app and chat' : 'Delete this chat'}>
                <IconButton
                  size="small"
                  aria-label="Delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTarget(s);
                  }}
                  sx={{ color: PG.muted }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ListItemButton>
          ))}
        </List>
      </Paper>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Paper variant="outlined" sx={{ px: 1.5, py: 1, mb: 1.25, bgcolor: PG.panel, borderColor: PG.border, color: PG.text, overflow: 'hidden' }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            useFlexGap
            sx={{
              flexWrap: 'wrap',
              rowGap: 1,
              minHeight: 52,
            }}
          >
            <Stack spacing={0.25} sx={{ pr: 0.5 }}>
              <Typography variant="caption" sx={{ color: PG.muted, fontWeight: 600, letterSpacing: '0.04em' }}>
                MODE
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={isBuilder ? 'app_builder' : 'chat'}
                onChange={(_, v) => {
                  if (v) updateSetting({ mode: v });
                }}
                sx={{
                  '& .MuiToggleButton-root': { color: PG.muted, borderColor: PG.border, py: 0.4, px: 1.1, whiteSpace: 'nowrap' },
                  '& .Mui-selected': { color: `${PG.text} !important`, bgcolor: 'rgba(56,189,248,0.16) !important' },
                }}
              >
                <Tooltip title="Ask questions and debug. Same knowledge as App Builder — your platform, pinned pages and pinned features all carry over, and the model can see the files in your project. It explains instead of rewriting them.">
                  <ToggleButton value="chat">Chat</ToggleButton>
                </Tooltip>
                <Tooltip title="Generate a real app (HTML/JS and more). Split view: chat on the left, live Preview on the right. Same knowledge and pins as Chat, plus the model writes and rewrites files. Attach a chain so Preview can run it as you.">
                  <ToggleButton value="app_builder">App Builder</ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>
            </Stack>
            <Stack spacing={0.25} sx={{ pl: 1, borderLeft: '1px solid', borderColor: PG.border }}>
              <Typography variant="caption" sx={{ color: PG.muted, fontWeight: 600, letterSpacing: '0.04em' }}>
                PLATFORM
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={sdkOn ? 'sdk' : nutritionOn ? 'nutrition' : 'easyai'}
                onChange={(_, v) => v && setStudioPlatform(v)}
                sx={{
                  '& .MuiToggleButton-root': { color: PG.muted, borderColor: PG.border, py: 0.4, px: 1, textTransform: 'none', whiteSpace: 'nowrap' },
                  '& .MuiToggleButton-root[value="easyai"].Mui-selected': {
                    color: `${PG.text} !important`,
                    bgcolor: 'rgba(56,189,248,0.16) !important',
                  },
                  '& .MuiToggleButton-root[value="nutrition"].Mui-selected': {
                    color: `${PG.text} !important`,
                    bgcolor: 'rgba(52,211,153,0.22) !important',
                    fontWeight: 800,
                  },
                  '& .MuiToggleButton-root[value="sdk"].Mui-selected': {
                    color: `${PG.text} !important`,
                    bgcolor: 'rgba(167,139,250,0.24) !important',
                    fontWeight: 800,
                  },
                }}
              >
                <Tooltip title="EasyAI only. The model will not load Nutrition or SDK docs, so chat stays focused on chains and functions.">
                  <ToggleButton value="easyai" sx={{ textTransform: 'none' }}>
                    EasyAI
                  </ToggleButton>
                </Tooltip>
                <Tooltip title="EasyAI plus the NutritionAI REST API, for web and backend apps. Paste your Nutrition license in Settings. The model learns token auth, semantic search, image scan, and ref-code details, and Preview proxies those calls. App Builder only.">
                  <ToggleButton value="nutrition" sx={{ textTransform: 'none', fontWeight: 800 }}>
                    + NutritionAI
                  </ToggleButton>
                </Tooltip>
                <Tooltip title="Mobile SDK mode, for iOS, Android, React Native or Flutter apps. The model learns SDK install, configure, camera, barcode and the food data model for the platform you pick in Settings. Works in chat for questions and in App Builder to scaffold an integration.">
                  <ToggleButton value="sdk" sx={{ textTransform: 'none', fontWeight: 800 }}>
                    Mobile SDK
                  </ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>
            </Stack>
            {isBuilder && (
              <Stack spacing={0.25} sx={{ pl: 1, borderLeft: '1px solid', borderColor: PG.border }}>
                <Typography variant="caption" sx={{ color: PG.muted, fontWeight: 600, letterSpacing: '0.04em' }}>
                  LAYOUT
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={workspace}
                  onChange={(_, v) => v && setWorkspace(v)}
                  sx={{
                    '& .MuiToggleButton-root': { color: PG.muted, borderColor: PG.border, py: 0.4, px: 1.1, whiteSpace: 'nowrap' },
                    '& .Mui-selected': { color: `${PG.text} !important`, bgcolor: 'rgba(56,189,248,0.16) !important' },
                  }}
                >
                  <Tooltip title="Chat only — hide the preview pane">
                    <ToggleButton value="chat">Messages</ToggleButton>
                  </Tooltip>
                  <Tooltip title="Chat on the left, live Preview on the right. Drag the divider to resize.">
                    <ToggleButton value="split">Split</ToggleButton>
                  </Tooltip>
                  <Tooltip title="Preview only — test the generated app full width">
                    <ToggleButton value="canvas">Preview</ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>
              </Stack>
            )}
            <Box className="toolbar-spacer" sx={{ flex: 1, minWidth: 8 }} />
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
            <Tooltip title="Model, then attach EasyAI chains or functions this app should wrap. Chat and preview stay the same size.">
              <Button
                size="small"
                variant="contained"
                startIcon={<SettingsIcon />}
                onClick={() => openSettings()}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: PG.accent,
                  color: '#0b1220',
                  px: 1.25,
                  minHeight: 32,
                  boxShadow: '0 0 0 3px rgba(56,189,248,0.28)',
                  '&:hover': { bgcolor: '#7dd3fc', boxShadow: '0 0 0 3px rgba(56,189,248,0.45)' },
                }}
              >
                Settings
              </Button>
            </Tooltip>
            {sdkOn && (
              <>
                <Tooltip title="Which mobile SDK to load deep knowledge for. Only the platforms you pick are injected, so the model is not buried in three other languages.">
                  <Chip
                    size="small"
                    icon={<PhoneIphoneIcon sx={{ fontSize: 16 }} />}
                    label={
                      sdkPlatforms.length
                        ? sdkPlatforms.map(sdkPlatformLabel).join(' + ')
                        : 'Mobile SDK — pick a platform'
                    }
                    onClick={() => (sdkPlatforms.length ? openSettings('sdk') : setSdkPickerOpen(true))}
                    sx={{
                      bgcolor: sdkPlatforms.length ? 'rgba(167,139,250,0.2)' : 'rgba(251,191,36,0.16)',
                      color: PG.text,
                      fontWeight: 700,
                      '& .MuiChip-label': { whiteSpace: 'nowrap' },
                    }}
                  />
                </Tooltip>
                <Tooltip title="Pin official Passio SDK documentation (GitBook markdown) — including the per-platform API Reference and Classes and Properties pages. The next send includes what you pin.">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ArticleIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setSdkDocsOpen(true)}
                    sx={{
                      ...pgBtn,
                      color: PG.text,
                      borderColor: 'rgba(167,139,250,0.45)',
                      fontWeight: 700,
                      py: 0.25,
                      minHeight: 28,
                    }}
                  >
                    SDK Docs
                  </Button>
                </Tooltip>
              </>
            )}
            {nutritionOn && (
              <>
              <Chip
                size="small"
                icon={<RestaurantMenuIcon sx={{ fontSize: 16 }} />}
                label={nutritionKeySet ? 'NutritionAI live' : 'NutritionAI — add license'}
                onClick={() => openSettings('nutrition')}
                sx={{
                  bgcolor: nutritionKeySet ? 'rgba(52,211,153,0.16)' : 'rgba(251,191,36,0.16)',
                  color: PG.text,
                  fontWeight: 700,
                  '& .MuiChip-label': { whiteSpace: 'nowrap' },
                }}
              />
              <Tooltip title="Search live NutritionAI OpenAPI routes. Add one to inject its exact request and response into this chat, or type @ in the composer.">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<MenuBookIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setNutritionCatalogOpen(true)}
                  sx={{
                    ...pgBtn,
                    color: PG.text,
                    borderColor: 'rgba(52,211,153,0.45)',
                    fontWeight: 700,
                    py: 0.25,
                    minHeight: 28,
                  }}
                >
                  Browse API
                </Button>
              </Tooltip>
              <Tooltip title="Optional. The official Passio docs are searched automatically on every message; pin a page here to force it in whole on the next send.">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ArticleIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setNutritionDocsOpen(true)}
                  sx={{
                    ...pgBtn,
                    color: PG.text,
                    borderColor: 'rgba(52,211,153,0.45)',
                    fontWeight: 700,
                    py: 0.25,
                    minHeight: 28,
                  }}
                >
                  Docs
                </Button>
              </Tooltip>
              </>
            )}
            <Tooltip title="Current model. Click Settings to switch models or attach chains and functions.">
              <Chip
                size="small"
                label={modelLabel}
                onClick={() => openSettings('general')}
                sx={{
                  bgcolor: 'rgba(56,189,248,0.12)',
                  color: PG.text,
                  fontWeight: 600,
                  maxWidth: 200,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Tooltip>
            {sessionCredits > 0 && (
              <Typography variant="caption" sx={{ color: PG.muted, whiteSpace: 'nowrap' }}>
                {formatCredits(sessionCredits)} this chat
              </Typography>
            )}
            <Tooltip title={expanded ? 'Exit full screen' : 'Use the whole screen'}>
              <IconButton size="small" onClick={() => setExpanded((v) => !v)} sx={{ color: PG.muted }}>
                {expanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Get started — pick EasyAI, NutritionAI or Mobile SDK, choose a target, and set up your first prompt">
              <IconButton
                size="small"
                onClick={() => {
                  setWizardPlatformHint(null);
                  setWizardOpen(true);
                }}
                sx={{ color: PG.muted }}
              >
                <RocketLaunchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export context for Cursor — pick chains, functions, routes and docs and download them as one Markdown file for your own project">
              <IconButton size="small" onClick={() => setContextExportOpen(true)} sx={{ color: PG.muted }}>
                <IosShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="How App Builder works — Settings, chat, Place, Mark, and export">
              <IconButton size="small" onClick={() => setGuideOpen(true)} sx={{ color: PG.muted }}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        <Dialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          maxWidth="md"
          fullWidth
          scroll="paper"
          PaperProps={{ sx: { bgcolor: PG.panel, color: PG.text, maxHeight: '86vh' } }}
        >
          <DialogTitle sx={{ pb: 0, pr: 2 }}>
            Settings
            <Typography variant="body2" sx={{ color: PG.muted, fontWeight: 400, mt: 0.5, lineHeight: 1.45 }}>
              <strong>General</strong> applies to every mode. The other tabs are the three platforms —
              only one is active at a time, and picking a tab's platform switches this session to it.
            </Typography>
            <Tabs
              value={settingsTab}
              onChange={(_, v) => setSettingsTab(v as SettingsTab)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                mt: 1.5,
                minHeight: 40,
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 40, color: PG.muted },
                '& .Mui-selected': { color: `${PG.text} !important` },
              }}
            >
              {SETTINGS_TABS.map((t) => {
                const isActivePlatform = t.id !== 'general' && t.id === activePlatformTab;
                const needsAttention = t.id === 'sdk' && sdkOn && sdkPlatforms.length === 0;
                return (
                  <Tab
                    key={t.id}
                    value={t.id}
                    label={
                      <Stack direction="row" spacing={0.6} alignItems="center">
                        <span>{t.label}</span>
                        {needsAttention ? (
                          <Box component="span" sx={{ color: '#fbbf24', fontSize: 16, lineHeight: 1 }}>
                            !
                          </Box>
                        ) : isActivePlatform ? (
                          <Box
                            component="span"
                            sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: t.dot }}
                          />
                        ) : null}
                      </Stack>
                    }
                  />
                );
              })}
            </Tabs>
          </DialogTitle>
          <DialogContent sx={{ pt: 2.5, pb: 2, overflowX: 'hidden' }} dividers>
            <Box hidden={settingsTab !== 'general'}>
            <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Model</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" sx={{ mb: 1.25 }}>
              <Box sx={{ flex: '1 1 240px', minWidth: 200, maxWidth: '100%' }}>
                <ModelSelector
                  value={settings.model}
                  onChange={onModelChange}
                  options={modelOptions}
                  size="small"
                  label="Model"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.45 }}>
                  {isImageModel
                    ? 'Image models only draw a picture from your prompt — they will not write an app or chat reply.'
                    : canSeeImages
                      ? 'This text model can look at images you attach. It cannot generate a new picture unless you switch to an image model.'
                      : 'Text model for chat and App Builder. For building apps, Claude Opus 5.5 is the default; Sonnet 5.5 is faster at about half the cost, and Fable 5.1 (when enabled) is the strongest for large or tricky builds.'}
                </Typography>
              </Box>
              <Box sx={{ flex: '1 1 280px', minWidth: 200 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
                  Filter the model list
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={intelFilter}
                  onChange={(_, v) => {
                    if (v) setIntelFilter(v);
                  }}
                  sx={{ flexWrap: 'wrap' }}
                >
                  {INTEL_FILTERS.map((f) => (
                    <ToggleButton key={f.id} value={f.id} sx={{ px: 1.1, fontSize: '0.72rem', textTransform: 'none' }}>
                      {f.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.45 }}>
                  Only narrows the Model dropdown by capability. It does not change the selected model or how it thinks.
                </Typography>
              </Box>
            </Stack>
            <Paper
              variant="outlined"
              sx={{ p: 1.75, mb: 2.5, bgcolor: 'transparent', borderColor: PG.border, maxWidth: 640 }}
            >
              {supportsEffort(settings.model) ? (
                <>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.25 }}>
                    Thinking effort · {modelOptions.find((o) => o.value === settings.model)?.label || settings.model}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.45 }}>
                    How long Claude thinks before it writes. Thinking counts toward the reply’s output limit and
                    your credits, so higher effort leaves less room for code in one reply. Also in the composer as
                    “Thinking”.
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={settings.effort || 'default'}
                    onChange={(_, v) => {
                      if (v) updateSetting({ effort: v === 'default' ? null : v });
                    }}
                    sx={{ flexWrap: 'wrap' }}
                  >
                    {EFFORT_OPTIONS.map((o) => (
                      <ToggleButton key={o.id} value={o.id} sx={{ px: 1.4, fontSize: '0.75rem', textTransform: 'none' }}>
                        {o.id === 'default' && isBuilder ? 'Default (Medium)' : o.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.45 }}>
                    {EFFORT_OPTIONS.find((o) => o.id === (settings.effort || 'default'))?.hint}{' '}
                    Temperature does not apply to this model.
                  </Typography>
                </>
              ) : (
                <>
                  <Box sx={{ maxWidth: 420 }}>
                    <TemperatureSlider
                      value={settings.temperature}
                      onChange={(v) => updateSetting({ temperature: v })}
                      model={settings.model}
                      size="small"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.45 }}>
                    Thinking effort is available on Claude models (Sonnet 5.5, Opus 5.5, Fable 5.1 and other
                    Claude 4.7+ models). This model picks its own reasoning depth.
                  </Typography>
                </>
              )}
            </Paper>

            {isBuilder ? (
              <Paper
                variant="outlined"
                sx={{ p: 1.75, mb: 2.5, bgcolor: 'transparent', borderColor: PG.border, maxWidth: 640 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!settings.smart_context}
                      onChange={(e) => updateSetting({ smart_context: e.target.checked })}
                    />
                  }
                  label={<Typography sx={{ fontWeight: 700, fontSize: 14 }}>Smart file selection (beta)</Typography>}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                  On larger projects, a small fast model reads your message plus a short note on each file and
                  picks which files the builder sees in full; the rest go as outlines. Cuts cost on big apps. Files
                  you name in your message, and small files, are always sent. If it can’t decide, everything is
                  sent as usual. Uses a few credits per message.
                </Typography>
              </Paper>
            ) : null}

            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>App output</Typography>
            {sdkOn ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5, lineHeight: 1.45 }}>
                In Mobile SDK mode the language follows the SDK platform. Change it on the Mobile SDK tab.
              </Typography>
            ) : (
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.45 }}>
                  What App Builder writes. Web targets render live in Preview; the others are code you export.
                  Same choice as the setup wizard.
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={studioTargetFor(settings.target_languages)?.id ?? null}
                  onChange={(_, v) => {
                    const t = STUDIO_TARGETS.find((x) => x.id === v);
                    if (t) updateSetting({ target_languages: t.langs });
                  }}
                  sx={{ flexWrap: 'wrap' }}
                >
                  {STUDIO_TARGETS.map((t) => (
                    <Tooltip key={t.id} title={t.blurb} placement="top" arrow>
                      <ToggleButton value={t.id} sx={{ px: 1.2, fontSize: '0.75rem', textTransform: 'none' }}>
                        {t.label}
                      </ToggleButton>
                    </Tooltip>
                  ))}
                </ToggleButtonGroup>
                {!studioTargetFor(settings.target_languages) && (settings.target_languages || []).length > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: PG.muted }}>
                    Current: {(settings.target_languages || []).join(', ')}
                  </Typography>
                )}
                {!isBuilder && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: PG.muted }}>
                    Applies when this session is in App Builder mode.
                  </Typography>
                )}
              </Box>
            )}
            </Box>

            <Box hidden={settingsTab !== 'easyai'}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2.5,
                borderColor: 'rgba(56,189,248,0.45)',
                bgcolor: 'rgba(56,189,248,0.08)',
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 1.25 }}>
                <AccountTreeIcon sx={{ color: PG.accent, mt: 0.25 }} />
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.3 }}>
                    Wrap EasyAI features
                  </Typography>
                  <Typography variant="body2" sx={{ color: PG.muted, mt: 0.4, lineHeight: 1.5 }}>
                    Attach the chains and functions this app should call. You can attach several.
                    Preview runs attached chains as you on this environment. Naming a chain in chat also works.
                  </Typography>
                  <Typography variant="body2" sx={{ color: PG.muted, mt: 1, lineHeight: 1.5 }}>
                    Need a new chain to wrap?{' '}
                    <Box
                      component="button"
                      onClick={() => {
                        setSettingsOpen(false);
                        setViewMode('chaining');
                        navigate('/dashboard');
                      }}
                      sx={{
                        border: 0,
                        p: 0,
                        bgcolor: 'transparent',
                        color: PG.accent,
                        font: 'inherit',
                        fontWeight: 800,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      Open Chain Builder
                    </Box>
                    {' '}to create one, then come back and attach it here.
                  </Typography>
                </Box>
              </Stack>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={attachKind}
                onChange={(_, v) => {
                  if (v) {
                    setAttachKind(v);
                    setCatalogQ('');
                    setCatalogOpen(true);
                  }
                }}
                sx={{ mb: 1.25, flexWrap: 'wrap' }}
              >
                <ToggleButton value="chain" sx={{ px: 1.5, textTransform: 'none', fontWeight: 700 }}>
                  Chains
                </ToggleButton>
                <ToggleButton value="function" sx={{ px: 1.5, textTransform: 'none' }}>
                  Functions
                </ToggleButton>
                <ToggleButton value="object" sx={{ px: 1.5, textTransform: 'none' }}>
                  Objects
                </ToggleButton>
              </ToggleButtonGroup>
              <TextField
                size="small"
                fullWidth
                placeholder={
                  attachKind === 'chain'
                    ? 'Search chains by name — click one to attach'
                    : attachKind === 'function'
                      ? 'Search AI functions by name'
                      : 'Search JSON objects (reply schema)'
                }
                value={catalogQ}
                onFocus={() => setCatalogOpen(true)}
                onChange={(e) => {
                  setCatalogQ(e.target.value);
                  setCatalogOpen(true);
                  loadCatalog(e.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              {catalogOpen && (
                <Paper variant="outlined" sx={{ mt: 1, maxHeight: 220, overflow: 'auto', bgcolor: PG.chat }}>
                  <List dense disablePadding>
                    {searchHits.length === 0 && (
                      <ListItemText
                        sx={{ px: 1.5, py: 1.25 }}
                        primary={catalogQ.trim() ? 'No matches' : `No ${attachKind}s yet — type a name to search`}
                        primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                      />
                    )}
                    {searchHits.map((h, i) => {
                      const latest =
                        searchHits.findIndex((x) => x.name.toLowerCase() === h.name.toLowerCase()) === i;
                      return (
                      <ListItemButton
                        key={`${h.type}-${h.id}`}
                        onClick={() => {
                          attachHit(h);
                          setCatalogOpen(false);
                          setCatalogQ('');
                        }}
                      >
                        <ListItemText
                          primary={h.name}
                          secondary={[
                            latest && (h.type === 'chain' || h.type === 'function') ? 'Latest' : h.type,
                            formatWhen(h.updated_at),
                            h.description,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                          primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                          secondaryTypographyProps={{ fontSize: 11, noWrap: true }}
                        />
                      </ListItemButton>
                      );
                    })}
                  </List>
                </Paper>
              )}
              <Stack direction="row" spacing={0.5} sx={{ mt: 1.25, flexWrap: 'wrap', gap: 0.5 }} alignItems="center">
                {namedAttach(settings.chain_ids, 'chain').map((x) => (
                  <Chip
                    key={x.id}
                    color="primary"
                    label={x.date ? `${x.name} (${x.date})` : x.name}
                    onDelete={() => removeAttach('chain_ids', x.id)}
                    sx={{ fontWeight: 700 }}
                  />
                ))}
                {namedAttach(settings.object_ids, 'object').map((x) => (
                  <Chip key={x.id} size="small" color="secondary" label={`object: ${x.name}`} onDelete={() => removeAttach('object_ids', x.id)} />
                ))}
                {namedAttach(settings.function_ids, 'function').map((x) => (
                  <Chip
                    key={x.id}
                    size="small"
                    label={x.date ? `fn: ${x.name} · ${x.date}` : `fn: ${x.name}`}
                    onDelete={() => removeAttach('function_ids', x.id)}
                  />
                ))}
                {settings.chain_builder_context?.name &&
                  !(settings.chain_ids || []).includes(settings.chain_builder_context.id) && (
                  <Chip
                    size="small"
                    color="success"
                    label={`builder context: ${settings.chain_builder_context.name}`}
                    onDelete={() => {
                      const next = { ...settings, chain_builder_context: null };
                      setSettings(next);
                      persistSettings(next);
                    }}
                  />
                )}
                {!(settings.chain_ids || []).length && attachKind === 'chain' && (
                  <Typography variant="caption" sx={{ color: PG.muted }}>
                    Nothing attached yet — search and click a chain above.
                  </Typography>
                )}
              </Stack>
              {(settings.chain_ids || []).length > 0 && (
                <Typography variant="body2" sx={{ display: 'block', mt: 1.25, color: PG.text, lineHeight: 1.5 }}>
                  Attached. Close Settings and describe the app in chat. Submit in Preview to run the chain as you.
                </Typography>
              )}
              {((settings.chain_ids || []).length > 0 || (settings.function_ids || []).length > 0) && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: PG.muted, lineHeight: 1.45 }}>
                  The next send includes that chain’s steps, inputs, and graph (or the function schema) in the hidden system prompt. Chat history only keeps the @name you typed — not the full dump.
                </Typography>
              )}
            </Paper>

            </Box>

            <Box hidden={settingsTab !== 'nutrition'}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2.5,
                  borderColor: nutritionOn ? 'rgba(52,211,153,0.55)' : PG.border,
                  bgcolor: nutritionOn ? 'rgba(52,211,153,0.08)' : 'transparent',
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <RestaurantMenuIcon sx={{ color: nutritionOn ? '#34d399' : PG.muted, mt: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.3 }}>
                      NutritionAI (Passio Platform)
                    </Typography>
                    <Typography variant="body2" sx={{ color: PG.muted, mt: 0.4, lineHeight: 1.5 }}>
                      Off by default so EasyAI chats stay small. Turn this on for token auth, search, scan, and food details. Pin specific OpenAPI routes (Browse API or @ in chat) so the model gets their exact inputs and outputs. This is a NutritionAI license, not an EasyAI API key.
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={nutritionOn ? 'on' : 'off'}
                      onChange={(_, v) => {
                        if (v) setStudioPlatform(v === 'on' ? 'nutrition' : 'easyai');
                      }}
                      sx={{ mt: 1.25 }}
                    >
                      <ToggleButton value="off" sx={{ textTransform: 'none', px: 1.5 }}>
                        EasyAI only
                      </ToggleButton>
                      <ToggleButton value="on" sx={{ textTransform: 'none', px: 1.5, fontWeight: 800 }}>
                        EasyAI + NutritionAI
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {nutritionOn && (
                      <>
                      <TextField
                        size="small"
                        fullWidth
                        type="password"
                        autoComplete="off"
                        label="NutritionAI license key"
                        placeholder="Paste the license used to mint Passio tokens"
                        value={settings.nutrition_license_key || ''}
                        onChange={(e) => updateSetting({ nutrition_license_key: e.target.value })}
                        sx={{ mt: 1.5 }}
                        helperText="Studio exchanges this for a Bearer token and proxies Preview. Exported apps still call the token URL themselves. Never commit the real key."
                      />
                      <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', gap: 0.75 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<MenuBookIcon />}
                          onClick={() => {
                            setSettingsOpen(false);
                            setNutritionCatalogOpen(true);
                          }}
                          sx={{ ...pgBtn, color: PG.text, borderColor: 'rgba(52,211,153,0.45)' }}
                        >
                          Browse Nutrition API
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ArticleIcon />}
                          onClick={() => {
                            setSettingsOpen(false);
                            setNutritionDocsOpen(true);
                          }}
                          sx={{ ...pgBtn, color: PG.text, borderColor: 'rgba(52,211,153,0.45)' }}
                        >
                          Passio GitBook
                        </Button>
                      </Stack>
                      </>
                    )}
                  </Box>
                </Stack>
              </Paper>
              {!isBuilder && (
                <Typography variant="caption" sx={{ color: PG.muted, display: 'block', mb: 2.5, lineHeight: 1.45 }}>
                  NutritionAI knowledge only loads in App Builder, so turning it on here also switches
                  this session to App Builder. For REST questions without building an app, stay on
                  EasyAI and just ask.
                </Typography>
              )}
            </Box>

            {/* Not gated on App Builder: SDK questions are a chat use case. */}
            <Box hidden={settingsTab !== 'sdk'}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2.5,
                borderColor: sdkOn ? 'rgba(167,139,250,0.55)' : PG.border,
                bgcolor: sdkOn ? 'rgba(167,139,250,0.08)' : 'transparent',
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <PhoneIphoneIcon sx={{ color: sdkOn ? '#a78bfa' : PG.muted, mt: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.3 }}>
                    Mobile SDK development
                  </Typography>
                  <Typography variant="body2" sx={{ color: PG.muted, mt: 0.4, lineHeight: 1.5 }}>
                    For native apps that embed the Passio Nutrition AI SDK rather than calling the REST
                    API. Loads install, configure, camera, barcode, and the food data model for the
                    platforms you pick below. Use it in chat to ask integration questions, or in App
                    Builder to scaffold a real integration.
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={sdkOn ? 'on' : 'off'}
                    onChange={(_, v) => {
                      if (v) setStudioPlatform(v === 'on' ? 'sdk' : 'easyai');
                    }}
                    sx={{ mt: 1.25 }}
                  >
                    <ToggleButton value="off" sx={{ textTransform: 'none', px: 1.5 }}>
                      Off
                    </ToggleButton>
                    <ToggleButton value="on" sx={{ textTransform: 'none', px: 1.5, fontWeight: 800 }}>
                      Mobile SDK mode
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {sdkOn && (
                    <>
                      <Typography variant="caption" sx={{ color: PG.text, display: 'block', mt: 1.75, fontWeight: 700 }}>
                        Platform — required
                      </Typography>
                      <Typography variant="caption" sx={{ color: PG.muted, display: 'block', mb: 0.75, lineHeight: 1.45 }}>
                        Only the ones you pick get injected, so the model is not buried in languages you
                        are not writing. This also sets the App Builder output language, so there is no
                        separate language list for SDK mode.
                      </Typography>
                      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                        {SDK_PLATFORMS.map((p) => {
                          const on = sdkPlatforms.includes(p.id);
                          return (
                            <Tooltip key={p.id} title={p.blurb}>
                              <Chip
                                size="small"
                                label={p.label}
                                onClick={() => toggleSdkPlatform(p.id)}
                                sx={{
                                  cursor: 'pointer',
                                  fontWeight: on ? 800 : 500,
                                  color: PG.text,
                                  bgcolor: on ? 'rgba(167,139,250,0.28)' : 'transparent',
                                  border: '1px solid',
                                  borderColor: on ? 'rgba(167,139,250,0.6)' : PG.border,
                                }}
                              />
                            </Tooltip>
                          );
                        })}
                      </Stack>
                      {sdkPlatforms.length === 0 && (
                        <Typography variant="caption" sx={{ color: '#fbbf24', display: 'block', mt: 1 }}>
                          Pick at least one — otherwise the model has to ask before it can write code.
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: PG.text, display: 'block', mt: 2, fontWeight: 700 }}>
                        Reference material
                      </Typography>
                      <Typography variant="caption" sx={{ color: PG.muted, display: 'block', mb: 1, lineHeight: 1.45 }}>
                        <strong>Features</strong> is the platform's capability list with the SDK method
                        for each one — pin what you are building so the model writes the right call and
                        knows the data it gets back. Official docs are searched automatically on every
                        message, so <strong>Docs</strong> is optional: use it to force a specific page in
                        whole.
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<MenuBookIcon />}
                          onClick={() => {
                            setSettingsOpen(false);
                            setSdkCapsOpen(true);
                          }}
                          sx={{ ...pgBtn, color: PG.text, borderColor: 'rgba(167,139,250,0.45)' }}
                        >
                          Browse SDK features
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ArticleIcon />}
                          onClick={() => {
                            setSettingsOpen(false);
                            setSdkDocsOpen(true);
                          }}
                          sx={{ ...pgBtn, color: PG.text, borderColor: 'rgba(167,139,250,0.45)' }}
                        >
                          Pin SDK docs
                        </Button>
                      </Stack>
                      {sdkCapabilityIds.length > 0 && (
                        <Stack direction="row" spacing={0.5} sx={{ mt: 1.25, flexWrap: 'wrap', gap: 0.5 }}>
                          {sdkCapabilityIds.map((id) => (
                            <Chip
                              key={id}
                              size="small"
                              label={sdkCapabilityLabel(id)}
                              onDelete={() => unpinSdkCapability(id)}
                              sx={{ bgcolor: 'rgba(167,139,250,0.22)', color: PG.text }}
                            />
                          ))}
                        </Stack>
                      )}
                      {(settings.sdk_doc_urls || []).length > 0 && (
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                          {(settings.sdk_doc_urls || []).map((url: string) => (
                            <Chip
                              key={url}
                              size="small"
                              variant="outlined"
                              label={docUrlLabel(url)}
                              onDelete={() => unpinSdkDoc(url)}
                              sx={{ color: PG.text, borderColor: 'rgba(167,139,250,0.45)' }}
                            />
                          ))}
                        </Stack>
                      )}
                    </>
                  )}
                </Box>
              </Stack>
            </Paper>
            </Box>

            <Box hidden={settingsTab !== 'easyai'}>
            {isBuilder && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>App languages</Typography>
                <Typography variant="caption" sx={{ color: PG.muted, display: 'block', mb: 1, lineHeight: 1.45 }}>
                  HTML/CSS/JS preview live in the canvas. Other languages still emit source files plus a sidecar HTML demo so Preview is never blank.
                </Typography>
                {BUILDER_LANG_GROUPS.map((group) => {
                  const selected: string[] = settings.target_languages || ['html', 'css', 'javascript'];
                  return (
                    <Box key={group.label} sx={{ mb: 0.75 }}>
                      <Typography variant="caption" sx={{ color: PG.muted, display: 'block', fontWeight: 600 }}>
                        {group.label}
                      </Typography>
                      <FormGroup row>
                        {group.langs.map((lang) => (
                          <FormControlLabel
                            key={lang.id}
                            control={
                              <Checkbox
                                size="small"
                                checked={selected.includes(lang.id)}
                                onChange={() => {
                                  const next = selected.includes(lang.id)
                                    ? selected.filter((x) => x !== lang.id)
                                    : [...selected, lang.id];
                                  updateSetting({ target_languages: next.length ? next : [lang.id] });
                                }}
                              />
                            }
                            label={lang.label}
                            sx={{ mr: 1.5, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: PG.text } }}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  );
                })}
              </Box>
            )}
            </Box>

            <Box hidden={settingsTab !== 'general'}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Knowledge (optional)</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.45 }}>
              Categories pull RAG chunks into each send. Not required for chain apps.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Autocomplete
                multiple
                size="small"
                sx={{ flex: 1, minWidth: 0 }}
                options={categories}
                getOptionLabel={(o) => o.name}
                value={categories.filter((c) => (settings.category_ids || []).includes(c.id))}
                onOpen={() => loadCatalog('')}
                onChange={(_, val) => updateSetting({ category_ids: val.map((v) => v.id) })}
                renderInput={(params) => (
                  <TextField {...params} label="Knowledge categories" placeholder="Search embeddings for this chat" />
                )}
              />
              <Button
                size="small"
                component="a"
                href="/dashboard/materials"
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon fontSize="small" />}
                sx={{ ...pgBtn, flexShrink: 0, color: PG.accent, whiteSpace: 'nowrap' }}
              >
                Add / edit knowledge
              </Button>
            </Stack>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={() => setSettingsOpen(false)} variant="contained" sx={{ fontWeight: 800, bgcolor: PG.accent, color: '#0b1220' }}>
              Done — back to building
            </Button>
          </DialogActions>
        </Dialog>

        <NutritionApiBrowser
          open={nutritionCatalogOpen}
          onClose={() => setNutritionCatalogOpen(false)}
          authHeaders={authHeaders}
          routes={napiRoutes}
          pinnedIds={settings.nutrition_route_ids || []}
          onUseRoute={(route) => {
            pinNutritionRoute(route, true);
          }}
        />
        {/* Same catalog, SDK framing: the SDK wraps these routes, so this doubles as
            the feature list without hand-maintaining an SDK method catalog. */}
        <NutritionApiBrowser
          open={sdkCapsOpen}
          onClose={() => setSdkCapsOpen(false)}
          authHeaders={authHeaders}
          endpoint="/api/v2/playground/sdk/capabilities"
          sdkMode
          title="Platform features and their SDK methods"
          blurb={
            <>
              Everything the platform can do, each mapped to the SDK method that wraps it. Add one and
              the next send gets the method name plus the exact data it returns. Rows marked{' '}
              <strong>REST</strong> have no SDK method — the model will wire a REST call instead of
              inventing one. You can also type <strong>@name</strong> in the composer.
            </>
          }
          pinnedIds={sdkCapabilityIds}
          onUseRoute={(route) => {
            pinSdkCapability(route, true);
          }}
        />
        <NutritionDocsBrowser
          open={nutritionDocsOpen}
          onClose={() => setNutritionDocsOpen(false)}
          authHeaders={authHeaders}
          pinnedUrls={settings.nutrition_doc_urls || []}
          onPin={pinNutritionDoc}
          onUnpin={unpinNutritionDoc}
        />
        <NutritionDocsBrowser
          open={sdkDocsOpen}
          onClose={() => setSdkDocsOpen(false)}
          authHeaders={authHeaders}
          endpoint="/api/v2/playground/sdk/docs"
          pinnedUrls={settings.sdk_doc_urls || []}
          onPin={pinSdkDoc}
          onUnpin={unpinSdkDoc}
          blurb="Official Passio mobile SDK docs as markdown (allowlisted). Pin the API Reference or Classes and Properties page for your platform so the model uses real method signatures instead of recalling them."
          searchPlaceholder="Search install, configure, barcode, api reference…"
        />
        <ChainPicker
          open={chainPickerOpen}
          onClose={() => setChainPickerOpen(false)}
          chains={catalogHits.filter((h) => h.type === 'chain')}
          attachedIds={settings.chain_ids || []}
          onAttach={(chain) => pinChain({ type: 'chain', ...chain }, 'dialog')}
          onDetach={(id) => removeAttach('chain_ids', id)}
        />

        {/* Required choice, not a nudge: SDK mode without a platform can only answer
            "which SDK are you using?", so ask up front instead of failing later. */}
        <Dialog
          open={sdkPickerOpen}
          onClose={cancelSdkPicker}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { bgcolor: PG.panel, color: PG.text } }}
        >
          <DialogTitle sx={{ pb: 0.5 }}>
            Which SDK are you using?
            <Typography variant="body2" sx={{ color: PG.muted, fontWeight: 400, mt: 0.5, lineHeight: 1.45 }}>
              Mobile SDK mode loads deep knowledge for one platform at a time — real class names,
              install steps, camera and barcode wiring. Pick yours and it is ready to answer.
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={1}>
              {SDK_PLATFORMS.map((p) => (
                <Paper
                  key={p.id}
                  variant="outlined"
                  onClick={() => chooseSdkPlatform(p.id)}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    bgcolor: 'transparent',
                    borderColor: PG.border,
                    '&:hover': { borderColor: '#a78bfa', bgcolor: 'rgba(167,139,250,0.08)' },
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <PhoneIphoneIcon fontSize="small" sx={{ color: '#a78bfa', mt: 0.25 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.92rem' }}>{p.label}</Typography>
                      <Typography variant="caption" sx={{ color: PG.muted, lineHeight: 1.45 }}>
                        {p.blurb}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
            <Typography variant="caption" sx={{ color: PG.muted, display: 'block', mt: 1.5, lineHeight: 1.45 }}>
              Building for more than one? Pick the main one now and add the rest under Settings →
              Mobile SDK.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={cancelSdkPicker} sx={{ ...pgBtn, color: PG.muted }}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="info" sx={{ mb: 1 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}
        {imageHint && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.75 }}>
              {canSeeImages
                ? `${modelLabel} can look at photos you attach, but it cannot draw a new picture. Playground image models only generate images (they will not write app code). Pick one:`
                : `${modelLabel} writes text. To generate a picture, switch to an image model:`}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {imageChoices.map((m) => (
                <Chip
                  key={m.apiName}
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`${m.displayName} · ${m.provider}`}
                  onClick={() => updateSetting({ model: m.apiName })}
                />
              ))}
            </Stack>
          </Alert>
        )}
        {isBuilder && isImageModel && (
          <Alert
            severity="info"
            sx={{ mb: 1 }}
            action={
              <Button color="inherit" size="small" onClick={() => onModelChange(DEFAULT_SETTINGS.model)}>
                Use a text model
              </Button>
            }
          >
            App Builder needs a text model so files can be written. An image model will only return a picture.
          </Alert>
        )}

        <Box ref={splitRef} sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 0 }}>
          {showChatPane && (
          <Box
            sx={{
              flex: showCanvas ? `0 0 ${chatPct}%` : 1,
              minWidth: showCanvas ? 240 : 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Paper
              ref={chatRef}
              variant="outlined"
              onScroll={() => {
                const el = chatRef.current;
                if (!el) return;
                pinToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
              }}
              sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: PG.chat, overflowAnchor: 'none', borderColor: PG.border }}
            >
              {messages.length === 0 && !streamingText && (
                <Box sx={{ textAlign: 'center', py: isBuilder ? 4 : 8, color: PG.muted }}>
                  <Typography variant="h6" gutterBottom>
                    {isBuilder ? 'EasyAI App Builder' : 'EasyAI Playground'}
                  </Typography>
                  {isBuilder ? (
                    <Box sx={{ maxWidth: 560, mx: 'auto', textAlign: 'left' }}>
                      <Typography variant="body2" sx={{ mb: 1.5, color: PG.muted, lineHeight: 1.55 }}>
                        Turn a chain or function into a live app.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.75, color: PG.text, lineHeight: 1.55 }}>
                        1.{' '}
                        <Box
                          component="button"
                          onClick={() => openSettings()}
                          sx={{
                            border: 0,
                            p: 0,
                            bgcolor: 'transparent',
                            color: PG.accent,
                            font: 'inherit',
                            fontWeight: 800,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                        >
                          Open Settings
                        </Box>
                        {' '}and attach one or more chains or functions this app should call.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.75, color: PG.text, lineHeight: 1.55 }}>
                        2. Describe what the app should do in chat — screens, branding, and how results should look.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1.5, color: PG.text, lineHeight: 1.55 }}>
                        3. Use Place and Mark on Preview to point at a region, then keep iterating.
                      </Typography>
                      {nutritionOn && (
                        <Typography variant="body2" sx={{ mb: 1.5, color: PG.text, lineHeight: 1.55 }}>
                          NutritionAI is on. Paste a license in Settings. Click <strong>Browse API</strong> or type <strong>@</strong> in chat to pin routes so the model gets their exact inputs and outputs. EasyAI chains still work in the same chat.
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => openSettings()}
                          sx={{ textTransform: 'none', fontWeight: 800, bgcolor: PG.accent, color: '#0b1220' }}
                        >
                          Open Settings
                        </Button>
                      </Stack>
                      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, textAlign: 'center', color: PG.muted, lineHeight: 1.5 }}>
                        Don’t have a chain yet?{' '}
                        <Box
                          component="button"
                          onClick={() => {
                            setViewMode('chaining');
                            navigate('/dashboard');
                          }}
                          sx={{
                            border: 0,
                            p: 0,
                            bgcolor: 'transparent',
                            color: PG.accent,
                            font: 'inherit',
                            fontWeight: 700,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                        >
                          Create one in Chain Builder
                        </Box>
                        , then attach it here. The ? in the header explains Place, Mark, and export.
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ mb: 2, maxWidth: 520, mx: 'auto' }}>
                      Pick a model, optionally attach knowledge, then send. Settings apply to the next message. For pictures, switch to an image model — vision text models can only look at photos you attach.
                    </Typography>
                  )}
                </Box>
              )}
              {chatBlocks.map((block) => {
                if (block.kind === 'compare') {
                  const stream =
                    compareStream && compareStream.groupId === block.groupId
                      ? compareStream
                      : null;
                  const lanes = stream
                    ? [
                        ...block.lanes,
                        {
                          id: '__stream__',
                          content: stream.text,
                          model_used: stream.model,
                          artifacts: [],
                        },
                      ]
                    : block.lanes;
                  const selected =
                    compareSel[block.groupId] && lanes.some((l) => l.id === compareSel[block.groupId])
                      ? compareSel[block.groupId]
                      : lanes[lanes.length - 1]?.id || '';
                  return (
                    <CompareReply
                      key={block.groupId}
                      lanes={lanes}
                      selectedId={selected}
                      onSelect={(id) => setCompareSel((s) => ({ ...s, [block.groupId]: id }))}
                      streaming={stream}
                      onRestore={askRestore}
                      restoring={restoring}
                      currentModel={settings.model}
                      onUseModel={async (model) => {
                        await onModelChange(model);
                        const name = MODEL_REGISTRY[model]?.displayName || model;
                        setNotice(`This chat now uses ${name}. Next messages go to that model.`);
                      }}
                    />
                  );
                }
                const m = block.message;
                return (
                <Box
                  key={m.id}
                  sx={{
                    display: 'flex',
                    justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                    mb: 1.5,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      maxWidth: '92%',
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: m.role === 'user' ? PG.user : PG.assistant,
                      color: PG.text,
                      border: '1px solid',
                      borderColor: PG.border,
                    }}
                  >
                    {m.role === 'assistant' ? (
                      <ChatMarkdown text={m.content || ''} />
                    ) : (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                        {m.content}
                      </Typography>
                    )}
                    {(m.attachments || [])
                      .filter((a) => a.kind === 'image' || (a.content_type || '').startsWith('image/'))
                      .map((a) => (
                        <Box key={a.url} component="img" src={a.url} alt={a.filename} sx={{ maxWidth: '100%', mt: 1, borderRadius: 1 }} />
                      ))}
                    {(m.artifacts || [])
                      .filter((a) => a.url && (a.kind === 'image' || a.kind === 'upload' || /\.(png|jpe?g|gif|webp|svg)$/i.test(a.url)))
                      .map((a) => (
                        <Box key={a.url} component="img" src={a.url} alt="generated" sx={{ maxWidth: '100%', mt: 1, borderRadius: 1 }} />
                      ))}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                      {m.settings_snapshot?.studio_step && (
                        <Chip size="small" label="Studio step" sx={{ height: 20, fontSize: 11, bgcolor: 'rgba(56,189,248,0.16)', color: PG.accent }} />
                      )}
                      {m.model_used && m.role === 'assistant' && !m.settings_snapshot?.studio_step && (
                        <Typography variant="caption" sx={{ color: PG.muted }}>
                          {MODEL_REGISTRY[m.model_used]?.displayName || m.model_used}
                          {m.usage?.credits_used != null ? ` · ${formatCredits(m.usage.credits_used)}` : ''}
                        </Typography>
                      )}
                      {m.role === 'user' && UUID_RE.test(m.id) && (
                        <Button
                          size="small"
                          startIcon={<CompareArrowsIcon />}
                          onClick={() => {
                            setCompareOpen(true);
                            setCompareModels((prev) =>
                              prev.length >= 2 ? prev : [settings.model, textModelOptions.find((o) => o.value !== settings.model)?.value || settings.model].filter(Boolean),
                            );
                            setReplayUserId(m.id);
                            setNotice('Pick 2–4 models under the composer, then Send to compare this turn.');
                          }}
                          sx={{ textTransform: 'none', color: PG.accent }}
                        >
                          Compare models
                        </Button>
                      )}
                      <Tooltip title="Copy message">
                        <IconButton
                          size="small"
                          onClick={() => navigator.clipboard.writeText(m.content || '')}
                          sx={{ color: PG.muted }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      {m.role === 'assistant' && messageHasAppFiles(m) && (
                        <Button
                          size="small"
                          startIcon={<HistoryIcon />}
                          disabled={restoring === m.id}
                          onClick={() => askRestore(m.id)}
                        >
                          {restoring === m.id ? 'Restoring…' : 'Restore app'}
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                </Box>
                );
              })}
              {compareStream && !chatBlocks.some((b) => b.kind === 'compare' && b.groupId === compareStream.groupId) && (
                <CompareReply
                  lanes={[
                    {
                      id: '__stream__',
                      content: compareStream.text,
                      model_used: compareStream.model,
                    },
                  ]}
                  selectedId="__stream__"
                  onSelect={() => undefined}
                  streaming={compareStream}
                />
              )}
              {(streamingText || generatingImage) && !compareStream && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
                  <Paper elevation={0} sx={{ maxWidth: '92%', p: 1.5, bgcolor: PG.assistant, border: '1px solid', borderColor: PG.border, borderRadius: 2, color: PG.text }}>
                    {generatingImage && !streamingText && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} />
                        <Typography variant="body2">Generating image…</Typography>
                      </Stack>
                    )}
                    {streamingText && <ChatMarkdown text={streamingText} streaming />}
                  </Paper>
                </Box>
              )}
              {sending && streamStartedAt != null && !compareStream && !generatingImage && (
                <WorkingIndicator
                  startedAt={streamStartedAt}
                  thinking={thinkingText}
                  streamingText={streamingText}
                  isBuilder={isBuilder}
                />
              )}
              <div ref={bottomRef} />
            </Paper>

            <Paper variant="outlined" sx={{ mt: 1.25, p: 1.25, bgcolor: PG.panel, borderColor: PG.border }}>
              {queued && (
                <Tooltip title="Click to pull it back into the composer to edit. The X drops it.">
                  <Chip
                    size="small"
                    icon={<PlaylistAddIcon sx={{ fontSize: 16 }} />}
                    label={`Queued: ${(queued.text || '').replace(/\s+/g, ' ').slice(0, 64) || `${queued.attachments.length} file(s)`}`}
                    onClick={() => {
                      setInput((prev) => prev || queued.text);
                      setPendingFiles((p) => (p.length ? p : queued.attachments));
                      queuedRef.current = null;
                      setQueued(null);
                    }}
                    onDelete={() => {
                      queuedRef.current = null;
                      setQueued(null);
                    }}
                    sx={{
                      mb: 1,
                      maxWidth: '100%',
                      bgcolor: 'rgba(56,189,248,0.14)',
                      color: PG.text,
                      fontWeight: 600,
                    }}
                  />
                </Tooltip>
              )}
              {pendingFiles.length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {pendingFiles.map((f) => (
                    <Chip
                      key={f.url}
                      size="small"
                      label={f.filename || 'file'}
                      onDelete={() => setPendingFiles((p) => p.filter((x) => x.url !== f.url))}
                      deleteIcon={<CloseIcon />}
                    />
                  ))}
                </Stack>
              )}
              {compareOpen && (
                <Box sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: PG.chat, border: '1px solid', borderColor: PG.border }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mb: 0.5, rowGap: 0.5 }}
                  >
                    <Typography variant="caption" sx={{ color: PG.accent, fontWeight: 700, flexShrink: 0 }}>
                      Compare this send
                    </Typography>
                    {replayUserId && (
                      <Chip size="small" label="Re-running a previous turn" onDelete={() => setReplayUserId(null)} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 8 }} />
                    <Button
                      size="small"
                      onClick={() => { setCompareOpen(false); setReplayUserId(null); }}
                      sx={{ textTransform: 'none', flexShrink: 0 }}
                    >
                      Hide
                    </Button>
                  </Stack>
                  <Typography variant="caption" sx={{ display: 'block', color: PG.muted, mb: 0.75, lineHeight: 1.45 }}>
                    2–4 text models. Replies group in one bubble — pick a model from the dropdown.
                  </Typography>
                  <Autocomplete
                    multiple
                    size="small"
                    options={textModelOptions}
                    getOptionLabel={(o) => o.label || o.value}
                    value={textModelOptions.filter((o) => compareModels.includes(o.value))}
                    onChange={(_, val) => setCompareModels(val.map((v) => v.value).slice(0, 4))}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Add models (max 4)" />
                    )}
                  />
                </Box>
              )}
              <Stack spacing={1}>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    if (e.target.files?.length) uploadFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }} alignItems="center">
                    {namedAttach(settings.chain_ids, 'chain').map((x) => (
                      <Chip
                        key={x.id}
                        size="small"
                        label={x.date ? `@${chainMentionSlug(x.name)} · ${x.date}` : `@${chainMentionSlug(x.name)}`}
                        onDelete={() => removeAttach('chain_ids', x.id)}
                        sx={{ bgcolor: 'rgba(56,189,248,0.16)', color: PG.text, fontWeight: 700 }}
                      />
                    ))}
                    {nutritionOn &&
                      pinnedNutritionRoutes.map((r) => (
                        <Chip
                          key={nutritionRouteKey(r)}
                          size="small"
                          label={`@${nutritionRouteAlias(r)}`}
                          onDelete={() => unpinNutritionRoute(nutritionRouteKey(r))}
                          sx={{ bgcolor: 'rgba(52,211,153,0.16)', color: PG.text, fontWeight: 700 }}
                        />
                      ))}
                    {nutritionOn &&
                      (settings.nutrition_doc_urls || []).map((url: string) => (
                        <Chip
                          key={url}
                          size="small"
                          label={gitbookChipLabel(url)}
                          onDelete={() => unpinNutritionDoc(url)}
                          sx={{ bgcolor: 'rgba(125,211,252,0.16)', color: PG.text, fontWeight: 700 }}
                        />
                      ))}
                    <Button
                      size="small"
                      onClick={() => {
                        void loadCatalog('');
                        setChainPickerOpen(true);
                      }}
                      sx={{ ...pgBtn, color: PG.muted, minHeight: 24, py: 0 }}
                    >
                      {(settings.chain_ids || []).length ? 'Add chain' : '@ chain'}
                    </Button>
                    {nutritionOn && (
                      <>
                        <Button
                          size="small"
                          onClick={() => setNutritionCatalogOpen(true)}
                          sx={{ ...pgBtn, color: PG.muted, minHeight: 24, py: 0 }}
                        >
                          {pinnedNutritionRoutes.length ? 'Add route' : '@ NutritionAI route'}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setNutritionDocsOpen(true)}
                          sx={{ ...pgBtn, color: PG.muted, minHeight: 24, py: 0 }}
                        >
                          GitBook
                        </Button>
                      </>
                    )}
                  </Stack>
                <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={12}
                  inputRef={composerRef}
                  placeholder={
                    replayUserId
                      ? 'Models selected above — press Compare to re-run that turn'
                      : sending
                        ? 'Type a follow-up — Enter queues it until this reply finishes'
                      : isImageModel
                      ? `Describe an image for ${modelLabel}…`
                      : nutritionOn
                        ? 'Describe the app… @ for a chain or NutritionAI route, or type gitbook for official docs'
                      : isBuilder
                        ? 'Describe the app… type @ to attach a chain'
                      : 'Message Studio… type @ to attach a chain'
                  }
                  value={input}
                  onChange={(e) => {
                    const v = e.target.value;
                    setInput(v);
                    const caret = e.target.selectionStart ?? v.length;
                    const hit = mentionAtCaret(v, caret);
                    setMentionQuery(hit ? hit.query : null);
                    setMentionIndex(0);
                  }}
                  onKeyDown={(e) => {
                    if (mentionQuery != null && mentionHits.length) {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setMentionIndex((i) => (i + 1) % mentionHits.length);
                        return;
                      }
                      if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setMentionIndex((i) => (i - 1 + mentionHits.length) % mentionHits.length);
                        return;
                      }
                      if (e.key === 'Enter' || e.key === 'Tab') {
                        e.preventDefault();
                        const item = mentionHits[mentionIndex] || mentionHits[0];
                        if (item.kind === 'route') pinMentionedRoute(item.route);
                        else pinChain(item.hit, 'mention');
                        return;
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setMentionQuery(null);
                        return;
                      }
                    }
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey || !e.shiftKey)) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      color: PG.text,
                      bgcolor: PG.chat,
                      alignItems: 'flex-start',
                    },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: PG.border },
                    '& textarea': { resize: 'vertical' },
                  }}
                />
                {mentionQuery != null && mentionHits.length > 0 && (
                  <Paper
                    elevation={8}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: '100%',
                      mb: 0.5,
                      zIndex: 8,
                      bgcolor: PG.panel,
                      border: '1px solid',
                      borderColor: PG.border,
                      maxHeight: 280,
                      overflow: 'auto',
                    }}
                  >
                    {mentionHits.map((item, i) => {
                      const isRoute = item.kind === 'route';
                      const hit = isRoute ? null : item.hit;
                      const key = isRoute ? nutritionRouteKey(item.route) : `${item.kind}-${hit!.id}`;
                      const primary = isRoute
                        ? `@${nutritionRouteAlias(item.route)}`
                        : hit!.name;
                      const when = hit ? formatWhen(hit.updated_at) : '';
                      const latest = !isRoute && item.latest;
                      const secondary = isRoute
                        ? item.route.summary
                        : [`@${chainMentionSlug(hit!.name)}`, when, latest ? 'latest' : '']
                            .filter(Boolean)
                            .join(' · ');
                      const chipLabel = isRoute ? item.route.method : item.kind === 'function' ? 'fn' : 'chain';
                      return (
                      <ListItemButton
                        key={key}
                        selected={i === mentionIndex}
                        onMouseDown={(ev) => {
                          ev.preventDefault();
                          if (item.kind === 'route') pinMentionedRoute(item.route);
                          else pinChain(item.hit, 'mention');
                        }}
                      >
                        <Chip
                          size="small"
                          label={chipLabel}
                          sx={{ mr: 1, minWidth: 48, fontWeight: 800, color: PG.accent, border: '1px solid', borderColor: PG.accent }}
                        />
                        <ListItemText
                          primary={primary}
                          secondary={secondary}
                          primaryTypographyProps={{ fontSize: 13, fontWeight: 700, color: PG.text }}
                          secondaryTypographyProps={{ fontSize: 12, color: PG.muted, noWrap: true }}
                        />
                      </ListItemButton>
                      );
                    })}
                  </Paper>
                )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                  <Tooltip title="Attach files or images to the next message only (not the project). For logos in the app, use Upload or Image on the Preview toolbar.">
                    <span>
                      <IconButton onClick={() => fileRef.current?.click()} sx={{ color: PG.muted }}>
                        <AttachFileIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={compareOpen ? 'Hide compare' : 'Send this prompt to several models and flip between replies'}>
                    <IconButton
                      disabled={sending || isImageModel}
                      onClick={() => {
                        setCompareOpen((v) => {
                          const next = !v;
                          if (next) {
                            setCompareModels((prev) =>
                              prev.length >= 2
                                ? prev
                                : [settings.model, textModelOptions.find((o) => o.value !== settings.model)?.value || ''].filter(Boolean),
                            );
                          }
                          return next;
                        });
                      }}
                      sx={{ color: compareOpen ? PG.accent : PG.muted }}
                    >
                      <CompareArrowsIcon />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ minWidth: 140, maxWidth: 220, flex: '0 1 200px' }}>
                    <ModelSelector
                      value={settings.model}
                      onChange={onModelChange}
                      options={modelOptions}
                      size="small"
                      label="Model"
                      showRatings={false}
                      fullWidth
                    />
                  </Box>
                  {supportsEffort(settings.model) && (
                    <EffortPicker
                      value={settings.effort}
                      onChange={(effort) => updateSetting({ effort })}
                      isBuilder={isBuilder}
                      disabled={sending}
                    />
                  )}
                  {isBuilder && !isImageModel && (
                    <BuildTierPicker value={settings.model} onChange={onModelChange} disabled={sending} />
                  )}
                  <Box sx={{ flex: 1 }} />
                  {sending ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      {(!!input.trim() || pendingFiles.length > 0) && !compareOpen && (
                        <Tooltip title="Send this after the current reply finishes. Queue again to replace it.">
                          <Button
                            variant="outlined"
                            onClick={() => send()}
                            startIcon={<PlaylistAddIcon />}
                            sx={{
                              ...pgBtn,
                              minWidth: 108,
                              height: 40,
                              fontWeight: 700,
                              color: PG.text,
                              borderColor: PG.accent,
                            }}
                          >
                            {queued ? 'Replace queue' : 'Queue'}
                          </Button>
                        </Tooltip>
                      )}
                      <Button
                        variant="contained"
                        onClick={() => {
                          skipQueueDrainRef.current = true;
                          abortRef.current?.abort();
                          if (queuedRef.current) {
                            setNotice('Stopped. Queued follow-up is waiting — press Send when you want it to go.');
                          }
                        }}
                        startIcon={<StopIcon />}
                        sx={{
                          ...pgBtn,
                          minWidth: 108,
                          height: 40,
                          fontWeight: 700,
                          bgcolor: '#fda4af',
                          color: '#0b1220',
                          boxShadow: 'none',
                          '&:hover': { bgcolor: '#fecdd3', boxShadow: 'none' },
                        }}
                      >
                        Stop
                      </Button>
                    </Stack>
                  ) : (
                  <Tooltip title={queued && !input.trim() ? 'Send the queued follow-up now' : 'Send the prompt. In App Builder, the reply should emit files into Preview. Then test the form there — Preview runs an attached chain as you.'}>
                    <span>
                  <Button
                    variant="contained"
                    onClick={() => send()}
                    disabled={!input.trim() && !replayUserId && !queued}
                    startIcon={queued && !input.trim() ? <PlaylistAddIcon /> : <SendIcon />}
                    sx={{
                      ...pgBtn,
                      minWidth: 108,
                      height: 40,
                      fontWeight: 700,
                      bgcolor: PG.accent,
                      color: '#0b1220',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#7dd3fc', boxShadow: 'none' },
                      '&.Mui-disabled': { bgcolor: 'rgba(56,189,248,0.22)', color: PG.muted },
                    }}
                  >
                    {replayUserId || compareOpen ? 'Compare' : queued && !input.trim() ? 'Send queued' : 'Send'}
                  </Button>
                    </span>
                  </Tooltip>
                  )}
                </Stack>
              </Stack>
            </Paper>
          </Box>
          )}
          {showChatPane && showCanvas && (
            <Box
              onMouseDown={(e) => {
                e.preventDefault();
                draggingSplit.current = true;
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';
              }}
              sx={{
                width: 10,
                flexShrink: 0,
                cursor: 'col-resize',
                alignSelf: 'stretch',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover div, &:active div': { bgcolor: 'primary.main' },
              }}
              title="Drag to resize chat and preview"
            >
              <Box sx={{ width: 3, height: 48, borderRadius: 2, bgcolor: 'divider' }} />
            </Box>
          )}
          {isBuilder && (
            <Box
              sx={{
                display: workspace === 'chat' ? 'none' : 'flex',
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
            <AppBuilderPane
              key={sessionId || 'none'}
              sessionId={sessionId}
              workspace={workspace}
              project={project}
              onSelectPreview={(path) =>
                setSettings((prev) => ({
                  ...prev,
                  project: { ...(prev.project || project), preview_path: path },
                }))
              }
              onSaveFile={saveProjectFile}
              onDeleteFile={deleteProjectFile}
              onAddFile={saveProjectFile}
              onRenameFile={renameProjectFile}
              onSaveNotes={saveNotes}
              onRestoreFromMessage={askRestore}
              restoringId={restoring}
              onGenerateImage={generateProjectImage}
              onGenerateVideo={generateProjectVideo}
              onUploadAsset={uploadProjectAsset}
              onRecordStudioStep={recordStudioStep}
              messages={messages}
              onStudioTarget={handleStudioTarget}
              onAskChat={(text) => {
                setWorkspace('split');
                void send(text);
              }}
              canvasHint={canvasHint}
              onDismissCanvasHint={() => setCanvasHint(null)}
              onPreviewChain={previewChain}
              onPreviewFunction={previewFunction}
              onPreviewNutrition={previewNutrition}
              onPreviewFeedback={previewFeedback}
              onImportFolder={askImportFolder}
              liveChain={(settings.chain_ids || []).some((id: string) => UUID_RE.test(String(id)))}
              liveFunction={(settings.function_ids || []).length > 0}
              liveNutrition={/* preview proxy is App Builder-only */ isBuilder && nutritionOn && nutritionKeySet}
              nutritionSig={
                (settings.nutrition_license_key || '').trim()
                  ? `${String(settings.nutrition_license_key).trim().length}:${String(settings.nutrition_license_key).trim().slice(-4)}`
                  : ''
              }
              chainIds={settings.chain_ids || []}
              chainNames={namedAttach(settings.chain_ids, 'chain').map((x) => x.name)}
              easyAiBase={studioEasyAiBase()}
              nutritionBase={studioNutritionBase()}
              customerId={getEffectiveCustomerId()}
              onDownloadZip={downloadZip}
              onExportPack={exportLocalPack}
              exportingPack={exporting}
              onExportCursorPack={exportCursorPack}
              exportingCursorPack={exportingCursor}
              onCopyCursorBrief={copyCursorBrief}
              generatingImage={generatingAsset}
            />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
    <ConfirmDialog
      open={!!deleteTarget}
      title={
        deleteTarget && sessionLooksBuilder(deleteTarget, sessionId, isBuilder)
          ? 'Delete this app?'
          : 'Delete this chat?'
      }
      body={(() => {
        if (!deleteTarget) return '';
        const name = deleteTarget.title?.trim() || 'Untitled';
        const builder = sessionLooksBuilder(deleteTarget, sessionId, isBuilder);
        const n = sessionFileCount(deleteTarget, deleteTarget.id === sessionId ? project : undefined);
        if (builder) {
          return `“${name}” will be permanently deleted${n ? `, including ${n} app file${n === 1 ? '' : 's'}` : ''}. Chat history is removed too. This cannot be undone.`;
        }
        return `“${name}” will be permanently deleted. This cannot be undone.`;
      })()}
      confirmLabel="Delete"
      busy={deleting}
      onClose={() => !deleting && setDeleteTarget(null)}
      onConfirm={async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setError(null);
        try {
          await deleteChat(deleteTarget.id);
          setDeleteTarget(null);
        } catch (e: any) {
          setError(e.message || 'Could not delete');
        } finally {
          setDeleting(false);
        }
      }}
    />
    <ConfirmDialog
      open={!!restoreAsk}
      title="Restore this version?"
      body="Preview files will be replaced with that reply’s snapshot. Chat history stays. You can restore another version from Notes if this is wrong."
      confirmLabel="Restore"
      tone="warn"
      onClose={() => setRestoreAsk(null)}
      onConfirm={() => {
        const id = restoreAsk;
        setRestoreAsk(null);
        if (id) restoreFromMessage(id);
      }}
    />
    <ConfirmDialog
      open={!!importAsk}
      title="Replace current app files?"
      body="Importing this folder replaces the files in Preview. Chat history is unchanged. You can restore an earlier version from Notes."
      confirmLabel="Import and replace"
      tone="warn"
      onClose={() => setImportAsk(null)}
      onConfirm={async () => {
        const list = importAsk;
        setImportAsk(null);
        if (list) await importFolder(list);
      }}
    />
    <Dialog
      open={guideOpen}
      onClose={() => setGuideOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: PG.panel, color: PG.text } }}
    >
      <DialogTitle>How App Builder works</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 800, mb: 0.4 }}>Settings — wrap an existing chain</Typography>
            <Typography variant="body2" sx={{ color: PG.muted, lineHeight: 1.55 }}>
              The Settings button (right side of the header, next to the model) is where you pick a model and attach functions. For chains, type <strong>@</strong> in chat or use <strong>@ chain</strong> under the composer — you don’t need to open Settings. Preview runs attached chains as you on this environment.
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, mb: 0.4 }}>No chain yet? Build one first</Typography>
            <Typography variant="body2" sx={{ color: PG.muted, lineHeight: 1.55 }}>
              App Builder wraps something you already have. To create new AI steps, open{' '}
              <Box
                component="button"
                onClick={() => {
                  setGuideOpen(false);
                  setViewMode('chaining');
                  navigate('/dashboard');
                }}
                sx={{
                  border: 0,
                  p: 0,
                  bgcolor: 'transparent',
                  color: PG.accent,
                  font: 'inherit',
                  fontWeight: 700,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Chain Builder
              </Box>
              , save a chain, then use <strong>Make an app</strong> there — or come back here and type <strong>@</strong> (or Add chain) to attach it.
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, mb: 0.4 }}>Chat — describe the product</Typography>
            <Typography variant="body2" sx={{ color: PG.muted, lineHeight: 1.55 }}>
              After attaching, tell chat what screens you want, the look, and how results should display. Files appear in Preview as they stream in. Replies are restore points — you can roll the canvas back without deleting chat. While a reply is streaming, type a follow-up and press Enter or Queue — it sends when this turn finishes. Stop keeps the queue so you can edit it first.
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, mb: 0.4 }}>Preview toolbar</Typography>
            <Typography variant="body2" sx={{ color: PG.muted, lineHeight: 1.55 }}>
              Tabs, phone/tablet width, and reload stay on the first row. Collapse the chevron to hide Place / Mark / export and give Preview more height.
            </Typography>
            <Typography variant="body2" sx={{ color: PG.muted, lineHeight: 1.55, mt: 0.5 }}>
              <strong>Place</strong> — click one element (logo, hero, heading) to generate an image or describe a change for that spot.
            </Typography>
            <Typography variant="body2" sx={{ color: PG.muted, lineHeight: 1.55, mt: 0.5 }}>
              <strong>Mark</strong> — drag a box over Preview (works even if the app is broken), write notes, send a layout sketch to chat. Esc cancels Place or Mark. If Preview fails, use <strong>Ask chat to fix</strong> on the error toast so chat gets the request/response.
            </Typography>
            <Typography variant="body2" sx={{ color: PG.muted, lineHeight: 1.55, mt: 0.5 }}>
              <strong>Upload</strong> — add a logo or photos to assets/. With Place on, they drop on the clicked element. Otherwise say where they go (header logo, hero) and Send to chat, or just keep them in the project.
            </Typography>
            <Typography variant="body2" sx={{ color: PG.muted, lineHeight: 1.55, mt: 0.5 }}>
              <strong>Import project</strong> — bring in an existing app folder, then describe where the chain should go.
            </Typography>
            <Typography variant="body2" sx={{ color: PG.muted, lineHeight: 1.55, mt: 0.5 }}>
              <strong>Export</strong> — zip with START_HERE and hosting files when it looks right. Submit in Preview to test attached chains for real.
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, mb: 0.4 }}>NutritionAI mode</Typography>
            <Typography variant="body2" sx={{ color: PG.muted, lineHeight: 1.55 }}>
              The header PLATFORM switch is EasyAI only until you pick EasyAI + NutritionAI. That injects Passio REST recipes plus a short live OpenAPI index. Type <strong>@</strong> for a chain or a NutritionAI route. Pin routes with <strong>Browse API</strong> so the next send includes that route’s request and response fields — not a guessed path. Pin official pages with <strong>Docs</strong> (Passio GitBook markdown), or type gitbook / look up docs in chat. Paste a NutritionAI license in Settings. It is not an EasyAI API key.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setGuideOpen(false);
            openSettings();
          }}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Open Settings
        </Button>
        <Button
          onClick={() => {
            setGuideOpen(false);
            setWizardPlatformHint(null);
            setWizardOpen(true);
          }}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Walk me through it
        </Button>
        <Button variant="contained" onClick={() => setGuideOpen(false)}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>

    <StudioWizard
      open={wizardOpen}
      onClose={() => setWizardOpen(false)}
      platformHint={wizardPlatformHint}
      licensePrefill={wizardLicensePrefill}
      currentLicense={settings.nutrition_license_key || ''}
      currentTargets={settings.target_languages || []}
      currentSdkPlatform={sdkPlatforms[0]}
      startsNewProject={messages.length > 0 || Object.keys(settings.project?.files || {}).length > 0}
      onApply={applyWizard}
      onOpenBrowseApi={() => setNutritionCatalogOpen(true)}
      onOpenDocs={() => setNutritionDocsOpen(true)}
      onOpenSdkDocs={() => setSdkDocsOpen(true)}
      onOpenSettings={() => openSettings()}
    />

    <ContextExportWizard
      open={contextExportOpen}
      onClose={() => setContextExportOpen(false)}
      authHeaders={authHeaders}
      initial={contextExportInitial}
    />
    </ThemeProvider>
  );
};

export default Playground;
